import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import StatusBadge from '../../components/common/StatusBadge';
import { CLIENT_STATUS_MAP } from '../../utils/constants';
import { formatCurrency, formatDate } from '../../utils/formatters';

function getClientPhotoUrl(photoPath, version) {
  if (!photoPath) return '';
  var path = String(photoPath).replace(/\\/g, '/');
  if (/^https?:\/\//i.test(path)) {
    return path + (path.indexOf('?') >= 0 ? '&' : '?') + 'v=' + encodeURIComponent(version || '1');
  }
  var apiBase = (api.defaults && api.defaults.baseURL) || process.env.REACT_APP_API_URL || '';
  var origin = '';
  if (/^https?:\/\//i.test(apiBase)) {
    origin = apiBase.replace(/\/api\/v1\/?$/, '').replace(/\/$/, '');
  } else if (process.env.REACT_APP_API_URL && /^https?:\/\//i.test(process.env.REACT_APP_API_URL)) {
    origin = process.env.REACT_APP_API_URL.replace(/\/api\/v1\/?$/, '').replace(/\/$/, '');
  } else if (typeof window !== 'undefined') {
    origin = window.location.origin;
  }
  var url = origin + (path.charAt(0) === '/' ? path : '/' + path);
  return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'v=' + encodeURIComponent(version || '1');
}

var COLORS = {
  primary: '#6366f1',
  primaryLight: '#eef2ff',
  primaryDark: '#4338ca',
  success: '#10b981',
  successLight: '#ecfdf5',
  successBorder: '#a7f3d0',
  warning: '#f59e0b',
  warningLight: '#fffbeb',
  warningBorder: '#fde68a',
  danger: '#ef4444',
  dangerLight: '#fef2f2',
  dangerBorder: '#fecaca',
  gray50: '#f8fafc',
  gray100: '#f1f5f9',
  gray200: '#e2e8f0',
  gray300: '#cbd5e1',
  gray400: '#94a3b8',
  gray500: '#64748b',
  gray600: '#475569',
  gray700: '#334155',
  gray800: '#1e293b',
  gray900: '#0f172a',
};

var INTERACTION_TYPES = [
  { value: 'note', label: 'Note', icon: '📝', color: '#6366f1', bg: '#eef2ff' },
  { value: 'call', label: 'Call', icon: '📞', color: '#059669', bg: '#ecfdf5' },
  { value: 'visit', label: 'Visit', icon: '🏠', color: '#d97706', bg: '#fffbeb' },
  { value: 'sms', label: 'SMS', icon: '💬', color: '#2563eb', bg: '#eff6ff' },
  { value: 'whatsapp', label: 'WhatsApp', icon: '📱', color: '#16a34a', bg: '#f0fdf4' },
  { value: 'email', label: 'Email', icon: '✉️', color: '#7c3aed', bg: '#f5f3ff' },
  { value: 'other', label: 'Other', icon: '📌', color: '#6b7280', bg: '#f9fafb' },
];

var DOC_TYPE_OPTIONS = [
  { value: 'national-id', label: 'National ID' },
  { value: 'id-front', label: 'National ID (Front)' },
  { value: 'id-back', label: 'National ID (Back)' },
  { value: 'passport', label: 'Passport Photo' },
  { value: 'utility-bill', label: 'Utility Bill' },
  { value: 'bank-statement', label: 'Bank Statement' },
  { value: 'payslip', label: 'Payslip' },
  { value: 'contract', label: 'Contract / Agreement' },
  { value: 'business-license', label: 'Business License' },
  { value: 'collateral-doc', label: 'Collateral Document' },
  { value: 'other', label: 'Other Document' },
];

var SECTIONS = [
  { id: 'personal', label: 'Personal Info', icon: '👤' },
  { id: 'nextOfKin', label: 'Next of Kin', icon: '👨‍👩‍👧' },
  { id: 'references', label: 'References', icon: '📋' },
  { id: 'homeAddress', label: 'Home Address', icon: '🏠' },
  { id: 'business', label: 'Business', icon: '💼' },
  { id: 'loanLimit', label: 'Loan Limit', icon: '💳' },
  { id: 'interactions', label: 'Interactions', icon: '💬', lazy: true },
  { id: 'collateral', label: 'Collateral', icon: '🔒', lazy: false },
  { id: 'documents', label: 'Documents', icon: '📄', lazy: true },
  { id: 'loans', label: 'Loan History', icon: '💰', lazy: true },
];

var INJECTED_STYLES = [
  '@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }',
  '@keyframes slideIn { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }',
  '@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }',
  '@keyframes zoomIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }',
  '@keyframes spin { to { transform: rotate(360deg); } }',
  '@keyframes skeleton-loading { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }',
  '::-webkit-scrollbar { width: 6px; height: 6px; }',
  '::-webkit-scrollbar-track { background: transparent; }',
  '::-webkit-scrollbar-thumb { background: ' + COLORS.gray300 + '; border-radius: 3px; }',
  '::-webkit-scrollbar-thumb:hover { background: ' + COLORS.gray400 + '; }',
  '@media (max-width: 768px) { .desktop-sidebar { display: none !important; } .mobile-sidebar-toggle { display: flex !important; } .action-bar-buttons { flex-wrap: wrap !important; } .action-bar-buttons button { flex: 1 1 auto !important; min-width: 120px !important; justify-content: center !important; } }',
  '@media (min-width: 769px) { .mobile-sidebar-toggle { display: none !important; } }',
].join('\n');

function SkeletonPulse(props) {
  return (
    <div style={Object.assign({
      width: props.width || '100%', height: props.height || 16, borderRadius: props.borderRadius || 6,
      background: 'linear-gradient(90deg, ' + COLORS.gray200 + ' 25%, ' + COLORS.gray100 + ' 50%, ' + COLORS.gray200 + ' 75%)',
      backgroundSize: '200% 100%', animation: 'skeleton-loading 1.5s ease-in-out infinite',
    }, props.style || {})} />
  );
}

function SectionCard(props) {
  return (
    <div style={{
      background: '#fff', borderRadius: 16, border: '1px solid ' + COLORS.gray200,
      overflow: 'hidden', transition: 'box-shadow 0.3s ease',
    }}>
      <div style={Object.assign({
        padding: '16px 20px', borderBottom: '1px solid ' + COLORS.gray100,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: COLORS.gray50, flexWrap: 'wrap', gap: 8,
      }, props.headerStyle || {})}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontSize: 20 }}>{props.icon}</span>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: COLORS.gray800 }}>
            {props.title}
            {props.count !== undefined && (
              <span style={{
                marginLeft: 10, fontSize: 12, fontWeight: 700,
                background: COLORS.primary, color: '#fff', padding: '2px 10px', borderRadius: 20,
              }}>{props.count}</span>
            )}
          </h3>
        </div>
        {props.action}
      </div>
      <div style={{ padding: 20 }}>{props.children}</div>
    </div>
  );
}

function InfoRow(props) {
  return (
    <div style={{ gridColumn: 'span ' + (props.cols || 1) }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: COLORS.gray400, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{props.label}</span>
      <p style={{ margin: '4px 0 0', fontSize: 14, color: COLORS.gray800, fontWeight: 500, wordBreak: 'break-word' }}>
        {props.value || <span style={{ color: COLORS.gray400, fontStyle: 'italic' }}>Not specified</span>}
      </p>
    </div>
  );
}

function EmptyState(props) {
  return (
    <div style={{
      textAlign: 'center', padding: '40px 20px', background: COLORS.gray50,
      borderRadius: 12, border: '2px dashed ' + COLORS.gray200,
    }}>
      <span style={{ fontSize: 40, display: 'block', marginBottom: 12 }}>{props.icon}</span>
      <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: COLORS.gray700 }}>{props.title}</p>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: COLORS.gray400 }}>{props.description}</p>
      {props.action}
    </div>
  );
}

function ClickableImage(props) {
  var _s = useState(false); var isHovered = _s[0]; var setIsHovered = _s[1];
  var _l = useState(false); var isLoaded = _l[0]; var setIsLoaded = _l[1];
  var w = props.width || 120; var h = props.height || 90; var br = props.borderRadius || 8;
  return (
    <div style={{
      position: 'relative', width: w, height: h, flexShrink: 0, borderRadius: br, overflow: 'hidden',
      cursor: 'pointer', border: '2px solid ' + (isHovered ? COLORS.primary : COLORS.gray200),
      transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)', transform: isHovered ? 'scale(1.05)' : 'scale(1)',
      boxShadow: isHovered ? '0 8px 25px rgba(99,102,241,0.2)' : '0 1px 3px rgba(0,0,0,0.08)',
      zIndex: isHovered ? 10 : 1, background: COLORS.gray100,
    }}
      onClick={function () { if (window.__openLightbox) window.__openLightbox({ src: props.src, caption: props.caption || '' }); }}
      onMouseEnter={function () { setIsHovered(true); }} onMouseLeave={function () { setIsHovered(false); }}
      title="Click to enlarge"
    >
      {!isLoaded && <SkeletonPulse width="100%" height="100%" borderRadius={br - 2} />}
      <img src={props.src} alt={props.alt || ''} onLoad={function () { setIsLoaded(true); }}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: isLoaded ? 'block' : 'none', transition: 'transform 0.3s', transform: isHovered ? 'scale(1.1)' : 'scale(1)' }} />
      <div style={{
        position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 50%)',
        opacity: isHovered ? 1 : 0, transition: 'opacity 0.25s', display: 'flex', alignItems: 'flex-end',
        justifyContent: 'center', paddingBottom: 8, pointerEvents: 'none',
      }}><span style={{ fontSize: 11, color: '#fff', fontWeight: 500 }}>🔍 View</span></div>
    </div>
  );
}

function getDocTypeLabel(type) {
  var opt = DOC_TYPE_OPTIONS.find(function (o) { return o.value === type; });
  return opt ? opt.label : (type || 'Unknown');
}

function getDocStatusConfig(status) {
  switch (status) {
    case 'verified': return { bg: '#ecfdf5', color: '#065f46', border: '#a7f3d0', label: '✅ Verified' };
    case 'rejected': return { bg: '#fef2f2', color: '#991b1b', border: '#fecaca', label: '❌ Rejected' };
    default: return { bg: '#fffbeb', color: '#92400e', border: '#fde68a', label: '⏳ Pending' };
  }
}

/* ═══════════════════════════════════════════════════════════════════════
   EDIT IDENTITY MODAL
   ═══════════════════════════════════════════════════════════════════════ */
function EditIdentityModal(props) {
  var isOpen = props.isOpen; var onClose = props.onClose; var onSave = props.onSave;
  var saving = props.saving; var initialData = props.client;

  var _form = useState({}); var formData = _form[0]; var setFormData = _form[1];
  var _errors = useState({}); var errors = _errors[0]; var setErrors = _errors[1];

  useEffect(function () {
    if (isOpen && initialData) {
      setFormData({ firstName: initialData.firstName || '', lastName: initialData.lastName || '', nationalId: initialData.nationalId || '', phone: initialData.phone || '' });
      setErrors({});
    }
  }, [isOpen, initialData]);

  var handleChange = function (field, value) {
    setFormData(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[field] = value; return n; });
    if (errors[field]) setErrors(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; delete n[field]; return n; });
  };

  var validate = function () {
    var e = {};
    if (!formData.firstName || !formData.firstName.trim()) e.firstName = 'First name is required';
    if (!formData.lastName || !formData.lastName.trim()) e.lastName = 'Last name is required';
    if (formData.nationalId && formData.nationalId.trim() && formData.nationalId.trim().length < 4) e.nationalId = 'ID number seems too short';
    if (!formData.phone || !formData.phone.trim()) e.phone = 'Phone number is required';
    else if (formData.phone.trim().length < 8) e.phone = 'Phone number seems too short';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  var handleSave = function () {
    if (!validate()) return;
    onSave({ firstName: formData.firstName.trim(), lastName: formData.lastName.trim(), nationalId: formData.nationalId.trim(), phone: formData.phone.trim() });
  };

  var inputBase = { width: '100%', padding: '10px 14px', borderRadius: 10, border: '2px solid ' + COLORS.gray200, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box', color: COLORS.gray800, transition: 'border-color 0.2s, box-shadow 0.2s' };
  var inputErr = Object.assign({}, inputBase, { border: '2px solid ' + COLORS.danger, background: COLORS.dangerLight });

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', animation: 'fadeIn 0.2s ease' }}
      onClick={function (e) { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 20, width: '90%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.3)', animation: 'zoomIn 0.25s ease' }}
        onClick={function (e) { e.stopPropagation(); }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + COLORS.gray100, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: COLORS.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>✏️</div>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: COLORS.gray800 }}>Edit Name, ID & Phone</h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: COLORS.gray400 }}>Fix spelling mistakes, wrong ID or wrong phone number</p>
            </div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: COLORS.gray100, color: COLORS.gray500, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        <div style={{ margin: '16px 24px 0', padding: '10px 14px', background: COLORS.warningLight, border: '1px solid #fde68a', borderRadius: 10, fontSize: 12, color: '#92400e', display: 'flex', alignItems: 'flex-start', gap: 8, lineHeight: 1.5 }}>
          <span style={{ flexShrink: 0 }}>⚠️</span>
          <div><strong>Important:</strong> Changes will be reflected across all associated loans and reports. Verify corrections before saving.</div>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {[
              { key: 'firstName', label: 'First Name', type: 'text', placeholder: 'Enter first name', req: true },
              { key: 'lastName', label: 'Last Name', type: 'text', placeholder: 'Enter last name', req: true },
              { key: 'nationalId', label: 'National ID Number', type: 'text', placeholder: 'e.g. 12345678', req: false, optional: true },
              { key: 'phone', label: 'Phone Number', type: 'tel', placeholder: 'e.g. 0712345678', req: true },
            ].map(function (f) {
              return (
                <div key={f.key}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.gray700, marginBottom: 6 }}>
                    {f.label} {f.req && <span style={{ color: COLORS.danger }}>*</span>}
                    {f.optional && <span style={{ fontSize: 10, color: COLORS.gray400, fontWeight: 400, marginLeft: 4 }}>(optional)</span>}
                  </label>
                  <input type={f.type} value={formData[f.key] || ''} onChange={function (e) { handleChange(f.key, e.target.value); }}
                    placeholder={f.placeholder} style={errors[f.key] ? inputErr : inputBase} autoFocus={f.key === 'firstName'}
                    onFocus={function (e) { if (!errors[f.key]) { e.target.style.borderColor = COLORS.primary; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.1)'; } }}
                    onBlur={function (e) { e.target.style.borderColor = errors[f.key] ? COLORS.danger : COLORS.gray200; e.target.style.boxShadow = 'none'; }} />
                  {errors[f.key] && <div style={{ fontSize: 12, color: COLORS.danger, marginTop: 4, fontWeight: 500 }}>{errors[f.key]}</div>}
                </div>
              );
            })}
          </div>
          {initialData && (
            <div style={{ marginTop: 20, padding: 14, background: COLORS.gray50, borderRadius: 10, border: '1px solid ' + COLORS.gray200 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>📋 Change Preview</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                {['firstName', 'lastName', 'nationalId', 'phone'].map(function (field) {
                  var labels = { firstName: 'First Name', lastName: 'Last Name', nationalId: 'National ID', phone: 'Phone' };
                  var oldVal = initialData[field] || '(empty)'; var newVal = formData[field] || '';
                  var changed = oldVal !== newVal;
                  return (
                    <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 85, color: COLORS.gray500, fontWeight: 500, flexShrink: 0 }}>{labels[field]}:</span>
                      {changed ? (
                        <span>
                          <span style={{ textDecoration: 'line-through', color: COLORS.danger, opacity: 0.7 }}>{oldVal}</span>
                          <span style={{ margin: '0 6px', color: COLORS.gray400 }}>→</span>
                          <span style={{ color: COLORS.success, fontWeight: 600 }}>{newVal || '(empty)'}</span>
                        </span>
                      ) : (
                        <span style={{ color: COLORS.gray400 }}>{oldVal} <span style={{ fontSize: 11 }}>(unchanged)</span></span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid ' + COLORS.gray100, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} disabled={saving} style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid ' + COLORS.gray200, background: '#fff', color: COLORS.gray600, fontSize: 14, fontWeight: 500, cursor: saving ? 'not-allowed' : 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: saving ? COLORS.gray300 : COLORS.primary, color: '#fff', fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: saving ? 'none' : '0 4px 12px rgba(99,102,241,0.3)' }}>
            {saving ? <span><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Saving...</span> : <span>✅ Save Changes</span>}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
   HOME & BUSINESS DETAILS MODAL
   ═══════════════════════════════════════════════════════════════════════ */
function ProfileDetailsModal(props) {
  var isOpen = props.isOpen; var onClose = props.onClose; var onSave = props.onSave;
  var saving = props.saving; var client = props.client;
  var _form = useState({}); var form = _form[0]; var setForm = _form[1];
  var _homeFile = useState(null); var homeFile = _homeFile[0]; var setHomeFile = _homeFile[1];
  var _businessFile = useState(null); var businessFile = _businessFile[0]; var setBusinessFile = _businessFile[1];
  var _error = useState(''); var error = _error[0]; var setError = _error[1];

  useEffect(function () {
    if (!isOpen || !client) return;
    var a = client.address || {};
    var hg = client.gpsLocation || {};
    var bg = client.businessGpsLocation || {};
    setForm({
      street: a.street || '',
      city: a.city || '',
      county: a.county || a.state || '',
      postalCode: a.postalCode || '',
      country: a.country || 'Kenya',
      type: a.type || '',
      description: a.description || '',
      homeDescription: client.homeDescription || '',
      homeLatitude: hg.latitude !== undefined && hg.latitude !== null ? String(hg.latitude) : '',
      homeLongitude: hg.longitude !== undefined && hg.longitude !== null ? String(hg.longitude) : '',
      businessName: client.businessName || '',
      businessType: client.businessType || '',
      businessSector: client.businessSector || '',
      businessAddress: client.businessAddress || '',
      businessDescription: client.businessDescription || '',
      businessLatitude: bg.latitude !== undefined && bg.latitude !== null ? String(bg.latitude) : '',
      businessLongitude: bg.longitude !== undefined && bg.longitude !== null ? String(bg.longitude) : '',
    });
    setHomeFile(null); setBusinessFile(null); setError('');
  }, [isOpen, client]);

  var change = function (key, value) {
    setForm(function (prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[key] = value; return n; });
    setError('');
  };

  var save = function () {
    var lat = form.homeLatitude === '' ? null : Number(form.homeLatitude);
    var lng = form.homeLongitude === '' ? null : Number(form.homeLongitude);
    var blat = form.businessLatitude === '' ? null : Number(form.businessLatitude);
    var blng = form.businessLongitude === '' ? null : Number(form.businessLongitude);

    if ((lat !== null && (Number.isNaN(lat) || lat < -90 || lat > 90)) ||
        (lng !== null && (Number.isNaN(lng) || lng < -180 || lng > 180)) ||
        (blat !== null && (Number.isNaN(blat) || blat < -90 || blat > 90)) ||
        (blng !== null && (Number.isNaN(blng) || blng < -180 || blng > 180))) {
      setError('Please enter valid GPS coordinates.');
      return;
    }

    onSave({
      address: {
        street: form.street.trim(),
        city: form.city.trim(),
        county: form.county.trim(),
        state: form.county.trim(),
        postalCode: form.postalCode.trim(),
        country: form.country.trim() || 'Kenya',
        type: form.type.trim(),
        description: form.description.trim(),
      },
      gpsLocation: { latitude: lat, longitude: lng },
      homeDescription: form.homeDescription.trim(),
      businessName: form.businessName.trim(),
      businessType: form.businessType.trim(),
      businessSector: form.businessSector.trim(),
      businessAddress: form.businessAddress.trim(),
      businessDescription: form.businessDescription.trim(),
      businessGpsLocation: { latitude: blat, longitude: blng },
    }, homeFile, businessFile);
  };

  if (!isOpen) return null;
  var input = { width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid ' + COLORS.gray200, fontSize: 13, boxSizing: 'border-box', outline: 'none', color: COLORS.gray800 };
  var label = { display: 'block', fontSize: 11, fontWeight: 700, color: COLORS.gray500, marginBottom: 5, textTransform: 'uppercase' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(15,23,42,.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={function(e){ if(e.target === e.currentTarget && !saving) onClose(); }}>
      <div style={{ width: 'min(900px, 100%)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 18, boxShadow: '0 25px 70px rgba(0,0,0,.28)' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid ' + COLORS.gray100, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
          <div>
            <h3 style={{ margin: 0, color: COLORS.gray800, fontSize: 17 }}>Update Home & Business Details</h3>
            <p style={{ margin: '4px 0 0', color: COLORS.gray400, fontSize: 12 }}>Admin and Branch Manager can update profile details and upload photos.</p>
          </div>
          <button onClick={onClose} disabled={saving} style={{ border: 0, background: COLORS.gray100, width: 34, height: 34, borderRadius: 8, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: 22 }}>
          {error && <div style={{ padding: 10, marginBottom: 16, borderRadius: 9, background: COLORS.dangerLight, color: '#991b1b', fontSize: 12 }}>{error}</div>}

          <div style={{ padding: 16, border: '1px solid ' + COLORS.gray200, borderRadius: 14, marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 14px', color: COLORS.gray800 }}>🏠 Home Details</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 12 }}>
              {[
                ['street','Street'], ['city','City'], ['county','Home County'], ['postalCode','Postal Code'],
                ['country','Country'], ['type','Address Type']
              ].map(function(item){
                return <div key={item[0]}><label style={label}>{item[1]}</label><input style={input} value={form[item[0]] || ''} onChange={function(e){change(item[0],e.target.value);}} /></div>;
              })}
              <div><label style={label}>Latitude</label><input style={input} type="number" step="any" value={form.homeLatitude || ''} onChange={function(e){change('homeLatitude',e.target.value);}} /></div>
              <div><label style={label}>Longitude</label><input style={input} type="number" step="any" value={form.homeLongitude || ''} onChange={function(e){change('homeLongitude',e.target.value);}} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={label}>Home Description</label><textarea style={Object.assign({},input,{minHeight:70,resize:'vertical'})} value={form.homeDescription || ''} onChange={function(e){change('homeDescription',e.target.value);}} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={label}>Address Description</label><textarea style={Object.assign({},input,{minHeight:60,resize:'vertical'})} value={form.description || ''} onChange={function(e){change('description',e.target.value);}} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={label}>Upload Home Photo</label><input type="file" accept="image/jpeg,image/png,image/webp" onChange={function(e){setHomeFile(e.target.files[0] || null);}} /><small style={{color:COLORS.gray400}}>{homeFile ? homeFile.name : 'Existing home photos are preserved.'}</small></div>
            </div>
          </div>

          <div style={{ padding: 16, border: '1px solid ' + COLORS.gray200, borderRadius: 14 }}>
            <h4 style={{ margin: '0 0 14px', color: COLORS.gray800 }}>💼 Business Details</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 12 }}>
              {[
                ['businessName','Business Name'], ['businessType','Business Type'], ['businessSector','Business Sector'], ['businessAddress','Business Address']
              ].map(function(item){
                return <div key={item[0]}><label style={label}>{item[1]}</label><input style={input} value={form[item[0]] || ''} onChange={function(e){change(item[0],e.target.value);}} /></div>;
              })}
              <div><label style={label}>Latitude</label><input style={input} type="number" step="any" value={form.businessLatitude || ''} onChange={function(e){change('businessLatitude',e.target.value);}} /></div>
              <div><label style={label}>Longitude</label><input style={input} type="number" step="any" value={form.businessLongitude || ''} onChange={function(e){change('businessLongitude',e.target.value);}} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={label}>Business Description</label><textarea style={Object.assign({},input,{minHeight:70,resize:'vertical'})} value={form.businessDescription || ''} onChange={function(e){change('businessDescription',e.target.value);}} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={label}>Upload Business Photo</label><input type="file" accept="image/jpeg,image/png,image/webp" onChange={function(e){setBusinessFile(e.target.files[0] || null);}} /><small style={{color:COLORS.gray400}}>{businessFile ? businessFile.name : 'Existing business photos are preserved.'}</small></div>
            </div>
          </div>
        </div>

        <div style={{ padding: '14px 22px', borderTop: '1px solid ' + COLORS.gray100, display: 'flex', justifyContent: 'flex-end', gap: 10, position: 'sticky', bottom: 0, background: '#fff' }}>
          <button onClick={onClose} disabled={saving} style={{ padding: '10px 18px', borderRadius: 9, border: '1px solid ' + COLORS.gray200, background: '#fff', cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding: '10px 20px', borderRadius: 9, border: 0, background: saving ? COLORS.gray300 : COLORS.primary, color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>{saving ? '⏳ Saving…' : '💾 Save Profile Details'}</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   BLACKLIST MODAL
   ═══════════════════════════════════════════════════════════════════════ */
function BlacklistModal(props) {
  var isOpen = props.isOpen; var onClose = props.onClose; var onSave = props.onSave;
  var saving = props.saving; var client = props.client; var isBlacklisted = props.isBlacklisted;
  var _reason = useState(''); var reason = _reason[0]; var setReason = _reason[1];
  var _error = useState(''); var error = _error[0]; var setError = _error[1];

  useEffect(function () { if (isOpen) { setReason(''); setError(''); } }, [isOpen]);

  var handleSave = function () {
    if (!isBlacklisted) {
      if (!reason.trim()) { setError('Please provide a reason for blacklisting this client.'); return; }
      if (reason.trim().length < 10) { setError('Reason must be at least 10 characters.'); return; }
    }
    onSave(reason.trim());
  };

  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', animation: 'fadeIn 0.2s ease' }}
      onClick={function (e) { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 20, width: '90%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.3)', animation: 'zoomIn 0.25s ease' }} onClick={function (e) { e.stopPropagation(); }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + COLORS.gray100, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: isBlacklisted ? COLORS.successLight : COLORS.dangerLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{isBlacklisted ? '✅' : '🚫'}</div>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: COLORS.gray800 }}>{isBlacklisted ? 'Remove from Blacklist' : 'Blacklist Client'}</h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: COLORS.gray400 }}>{client ? client.firstName + ' ' + client.lastName : ''} · {client ? client.clientNumber : ''}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: COLORS.gray100, color: COLORS.gray500, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        {!isBlacklisted && (
          <div style={{ margin: '16px 24px 0', padding: '12px 14px', background: COLORS.dangerLight, border: '1px solid ' + COLORS.danger, borderRadius: 10, fontSize: 12, color: '#991b1b', display: 'flex', alignItems: 'flex-start', gap: 8, lineHeight: 1.5 }}>
            <span style={{ flexShrink: 0, fontSize: 16 }}>⚠️</span>
            <div><strong>Blacklisting this client will:</strong><ul style={{ margin: '6px 0 0 16px', padding: 0 }}><li>Prevent them from applying for new loans</li><li>Flag their profile across the entire system</li><li>Notify all branch managers</li><li>Remain in effect until manually removed</li></ul></div>
          </div>
        )}
        {isBlacklisted && (
          <div style={{ margin: '16px 24px 0', padding: '12px 14px', background: COLORS.successLight, border: '1px solid ' + COLORS.success, borderRadius: 10, fontSize: 12, color: '#065f46', display: 'flex', alignItems: 'flex-start', gap: 8, lineHeight: 1.5 }}>
            <span style={{ flexShrink: 0, fontSize: 16 }}>✅</span>
            <div><strong>Removing from blacklist</strong> will restore this client's ability to apply for loans.</div>
          </div>
        )}
        {isBlacklisted && client && client.blacklistInfo && (
          <div style={{ margin: '16px 24px 0', padding: 14, background: COLORS.gray50, borderRadius: 10, border: '1px solid ' + COLORS.gray200 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray500, marginBottom: 8, textTransform: 'uppercase' }}>Current Blacklist Record</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
              <div><span style={{ color: COLORS.gray500, fontWeight: 500 }}>Reason: </span><span style={{ color: COLORS.gray800 }}>{client.blacklistInfo.reason || '—'}</span></div>
              <div><span style={{ color: COLORS.gray500, fontWeight: 500 }}>Date: </span><span style={{ color: COLORS.gray800 }}>{client.blacklistInfo.blacklistedAt ? formatDate(client.blacklistInfo.blacklistedAt) : '—'}</span></div>
              <div><span style={{ color: COLORS.gray500, fontWeight: 500 }}>By: </span><span style={{ color: COLORS.gray800 }}>{client.blacklistInfo.blacklistedBy ? (client.blacklistInfo.blacklistedBy.firstName || '') + ' ' + (client.blacklistInfo.blacklistedBy.lastName || '') : '—'}</span></div>
            </div>
          </div>
        )}
        <div style={{ padding: '20px 24px' }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.gray700, marginBottom: 6 }}>
            {isBlacklisted ? 'Reason for Removal' : 'Reason for Blacklisting'} {(!isBlacklisted) && <span style={{ color: COLORS.danger }}>*</span>}
          </label>
          <textarea value={reason} onChange={function (e) { setReason(e.target.value); setError(''); }}
            placeholder={isBlacklisted ? 'e.g. Client has settled all outstanding loans...' : 'e.g. Client has defaulted on loan and has been unresponsive for 30+ days...'}
            rows={4} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '2px solid ' + (error ? COLORS.danger : COLORS.gray200), fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box', color: COLORS.gray800, resize: 'vertical', fontFamily: 'inherit' }}
            onFocus={function (e) { if (!error) { e.target.style.borderColor = COLORS.primary; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.1)'; } }}
            onBlur={function (e) { e.target.style.borderColor = error ? COLORS.danger : COLORS.gray200; e.target.style.boxShadow = 'none'; }} />
          {error && <div style={{ fontSize: 12, color: COLORS.danger, marginTop: 6, fontWeight: 500 }}>{error}</div>}
          <div style={{ fontSize: 11, color: COLORS.gray400, marginTop: 4 }}>{reason.length} characters</div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid ' + COLORS.gray100, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} disabled={saving} style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid ' + COLORS.gray200, background: '#fff', color: COLORS.gray600, fontSize: 14, fontWeight: 500, cursor: saving ? 'not-allowed' : 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: saving ? COLORS.gray300 : (isBlacklisted ? COLORS.success : COLORS.danger), color: '#fff', fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: saving ? 'none' : (isBlacklisted ? '0 4px 12px rgba(16,185,129,0.3)' : '0 4px 12px rgba(239,68,68,0.3)') }}>
            {saving ? <span><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Processing...</span> : isBlacklisted ? <span>✅ Remove from Blacklist</span> : <span>🚫 Confirm Blacklist</span>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   LOAN LIMIT MODAL
   ═══════════════════════════════════════════════════════════════════════ */
function LoanLimitModal(props) {
  var isOpen = props.isOpen; var onClose = props.onClose; var onSave = props.onSave;
  var saving = props.saving; var client = props.client;
  var _limit = useState(''); var newLimit = _limit[0]; var setNewLimit = _limit[1];
  var _reason = useState(''); var reason = _reason[0]; var setReason = _reason[1];
  var _error = useState(''); var error = _error[0]; var setError = _error[1];

  useEffect(function () {
    if (isOpen && client) {
      var effective = (client.customLoanLimit && client.customLoanLimit > 0) ? client.customLoanLimit : (client.recommendedLoanLimit || 0);
      setNewLimit(effective > 0 ? String(effective) : '');
      setReason(client.customLimitReason || ''); setError('');
    }
  }, [isOpen, client]);

  var handleSave = function () {
    var v = parseInt(newLimit, 10);
    if (!newLimit.trim()) { setError('Please enter a loan limit amount.'); return; }
    if (isNaN(v) || v < 0) { setError('Please enter a valid positive number.'); return; }
    if (v > 10000000) { setError('Amount seems unusually high.'); return; }
    if (!reason.trim()) { setError('Please provide a reason.'); return; }
    if (reason.trim().length < 5) { setError('Reason must be at least 5 characters.'); return; }
    onSave(v, reason.trim());
  };

  var inputBase = { width: '100%', padding: '10px 14px', borderRadius: 10, border: '2px solid ' + COLORS.gray200, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box', color: COLORS.gray800, transition: 'border-color 0.2s, box-shadow 0.2s' };

  if (!isOpen) return null;
  var approvedLimit = client ? (client.recommendedLoanLimit || 0) : 0;
  var currentCustom = client ? (client.customLoanLimit || 0) : 0;
  var effective = currentCustom > 0 ? currentCustom : approvedLimit;
  var productName = client && client.recommendedLoanProduct ? (client.recommendedLoanProduct.name || '') : '';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', animation: 'fadeIn 0.2s ease' }}
      onClick={function (e) { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 20, width: '90%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.3)', animation: 'zoomIn 0.25s ease' }} onClick={function (e) { e.stopPropagation(); }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + COLORS.gray100, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: COLORS.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>💳</div>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: COLORS.gray800 }}>Adjust Loan Limit</h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: COLORS.gray400 }}>Increase or decrease the client's borrowing limit</p>
            </div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: COLORS.gray100, color: COLORS.gray500, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px 0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div style={{ padding: 14, background: COLORS.gray50, borderRadius: 10, border: '1px solid ' + COLORS.gray200 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.gray400, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Approved at Review</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.gray700, marginTop: 4 }}>{formatCurrency(approvedLimit)}</div>
              {productName && <div style={{ fontSize: 10, color: COLORS.gray400, marginTop: 2 }}>{productName}</div>}
            </div>
            <div style={{ padding: 14, background: currentCustom > 0 ? COLORS.warningLight : COLORS.gray50, borderRadius: 10, border: '1px solid ' + (currentCustom > 0 ? '#fde68a' : COLORS.gray200) }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: currentCustom > 0 ? '#92400e' : COLORS.gray400, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Current Override</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: currentCustom > 0 ? COLORS.warning : COLORS.gray400, marginTop: 4 }}>{currentCustom > 0 ? formatCurrency(currentCustom) : 'None'}</div>
            </div>
            {/* FIX #1: was '1px solid '#c7d2fe' (two unjoined string literals) */}
            <div style={{ padding: 14, background: COLORS.primaryLight, borderRadius: 10, border: '1px solid #c7d2fe' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.primaryDark, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Effective Limit</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.primary, marginTop: 4 }}>{formatCurrency(effective)}</div>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.gray700, marginBottom: 6 }}>New Loan Limit (KES) <span style={{ color: COLORS.danger }}>*</span></label>
            <input type="number" value={newLimit} onChange={function (e) { setNewLimit(e.target.value); setError(''); }} placeholder="e.g. 50000" style={Object.assign({}, inputBase, error ? { border: '2px solid ' + COLORS.danger, background: COLORS.dangerLight } : {})} onFocus={function (e) { if (!error) { e.target.style.borderColor = COLORS.primary; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.1)'; } }} onBlur={function (e) { e.target.style.borderColor = error ? COLORS.danger : COLORS.gray200; e.target.style.boxShadow = 'none'; }} />
            {error && <div style={{ fontSize: 12, color: COLORS.danger, marginTop: 6, fontWeight: 500 }}>{error}</div>}
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.gray700, marginBottom: 6 }}>Reason for Adjustment <span style={{ color: COLORS.danger }}>*</span></label>
            <textarea value={reason} onChange={function (e) { setReason(e.target.value); setError(''); }} placeholder="e.g. Client has demonstrated excellent repayment history..." rows={3} style={Object.assign({}, inputBase, { resize: 'vertical', fontFamily: 'inherit' })} onFocus={function (e) { e.target.style.borderColor = COLORS.primary; }} onBlur={function (e) { e.target.style.borderColor = COLORS.gray200; e.target.style.boxShadow = 'none'; }} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray500, marginBottom: 8 }}>Quick Presets:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {[10000, 25000, 50000, 100000, 200000, 500000].map(function (amt) {
                var sel = newLimit === String(amt);
                return (
                  <button key={amt} onClick={function () { setNewLimit(String(amt)); setError(''); }} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid ' + (sel ? COLORS.primary : COLORS.gray200), background: sel ? COLORS.primaryLight : '#fff', color: sel ? COLORS.primary : COLORS.gray600, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {formatCurrency(amt)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid ' + COLORS.gray100, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} disabled={saving} style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid ' + COLORS.gray200, background: '#fff', color: COLORS.gray600, fontSize: 14, fontWeight: 500, cursor: saving ? 'not-allowed' : 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: saving ? COLORS.gray300 : COLORS.primary, color: '#fff', fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: saving ? 'none' : '0 4px 12px rgba(99,102,241,0.3)' }}>
            {saving ? <span><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Saving...</span> : <span>💳 Save Limit</span>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */
export default function ClientDetail() {
  var params = useParams(); var id = params.id; var navigate = useNavigate();

  var _client = useState(null); var client = _client[0]; var setClient = _client[1];
  var _section = useState('personal'); var activeSection = _section[0]; var setActiveSection = _section[1];
  var _loaded = useState(function () { return new Set(['personal']); }); var loadedSections = _loaded[0]; var setLoadedSections = _loaded[1];
  var _collapsed = useState(false); var sidebarCollapsed = _collapsed[0]; var setSidebarCollapsed = _collapsed[1];
  var _mobile = useState(false); var isMobileSidebarOpen = _mobile[0]; var setIsMobileSidebarOpen = _mobile[1];

  var _docs = useState([]); var docs = _docs[0]; var setDocs = _docs[1];
  var _docsL = useState(false); var docsLoading = _docsL[0]; var setDocsLoading = _docsL[1];
  var _uploadDocType = useState(''); var uploadDocType = _uploadDocType[0]; var setUploadDocType = _uploadDocType[1];
  var _uploadDocNotes = useState(''); var uploadDocNotes = _uploadDocNotes[0]; var setUploadDocNotes = _uploadDocNotes[1];
  var _uploadingDoc = useState(false); var uploadingDoc = _uploadingDoc[0]; var setUploadingDoc = _uploadingDoc[1];
  var _uploadSuccess = useState(false); var uploadSuccess = _uploadSuccess[0]; var setUploadSuccess = _uploadSuccess[1];
  var _uploadError = useState(''); var uploadError = _uploadError[0]; var setUploadError = _uploadError[1];
  var docFileRef = useRef(null);
  var profilePictureInputRef = useRef(null);
  var _profilePictureUploading = useState(false); var profilePictureUploading = _profilePictureUploading[0]; var setProfilePictureUploading = _profilePictureUploading[1];

  var _loans = useState([]); var loans = _loans[0]; var setLoans = _loans[1];
  var _loansL = useState(false); var loansLoading = _loansL[0]; var setLoansLoading = _loansL[1];
  var _loanSummary = useState(null); var loanSummary = _loanSummary[0]; var setLoanSummary = _loanSummary[1];

  var _inter = useState([]); var interactions = _inter[0]; var setInteractions = _inter[1];
  var _interL = useState(false); var interactionsLoading = _interL[0]; var setInteractionsLoading = _interL[1];

  var _recov = useState(false); var recoveringRefs = _recov[0]; var setRecoveringRefs = _recov[1];
  var _lb = useState(null); var lightbox = _lb[0]; var setLightbox = _lb[1];
  var _dlDoc = useState(null); var downloadingDocId = _dlDoc[0]; var setDownloadingDocId = _dlDoc[1];

  var _modal = useState(false); var interactionModalOpen = _modal[0]; var setInteractionModalOpen = _modal[1];
  var _iType = useState('note'); var newInteractionType = _iType[0]; var setNewInteractionType = _iType[1];
  var _iContent = useState(''); var newInteractionContent = _iContent[0]; var setNewInteractionContent = _iContent[1];
  var _saving = useState(false); var savingInteraction = _saving[0]; var setSavingInteraction = _saving[1];
  var _expanded = useState(null); var expandedInteraction = _expanded[0]; var setExpandedInteraction = _expanded[1];
  var _filter = useState('all'); var interactionFilter = _filter[0]; var setInteractionFilter = _filter[1];

  var _editModal = useState(false); var editModalOpen = _editModal[0]; var setEditModalOpen = _editModal[1];
  var _savingClient = useState(false); var savingClient = _savingClient[0]; var setSavingClient = _savingClient[1];
  var _editSuccess = useState(false); var editSuccess = _editSuccess[0]; var setEditSuccess = _editSuccess[1];
  var _profileModal = useState(false); var profileModalOpen = _profileModal[0]; var setProfileModalOpen = _profileModal[1];

  var _blModal = useState(false); var blacklistModalOpen = _blModal[0]; var setBlacklistModalOpen = _blModal[1];
  var _blLoading = useState(false); var blacklistLoading = _blLoading[0]; var setBlacklistLoading = _blLoading[1];

  var _limitModal = useState(false); var limitModalOpen = _limitModal[0]; var setLimitModalOpen = _limitModal[1];
  var _limitLoading = useState(false); var limitLoading = _limitLoading[0]; var setLimitLoading = _limitLoading[1];
  var _officers = useState([]); var officers = _officers[0]; var setOfficers = _officers[1];
  var _assigning = useState(false); var assigning = _assigning[0]; var setAssigning = _assigning[1];

  var auth = useAuth();
  var currentUser = auth.user || {};

  var recoveredClientIdRef = useRef(null);

  var userRoleName = ((currentUser.roleName || (currentUser.role && currentUser.role.name) || currentUser.role || '') || '').toString().toLowerCase();
  var canBlacklist = ['admin', 'general manager', 'manager', 'branch manager'].includes(userRoleName);
  var canSetLimit = ['admin', 'general manager', 'manager', 'branch manager'].includes(userRoleName);
  var canReassign = canSetLimit;
  var canEditProfileDetails = ['admin', 'branch manager'].includes(userRoleName);
  var canEditClient = !userRoleName ||
    userRoleName === 'admin' ||
    userRoleName === 'general manager' ||
    userRoleName === 'loan officer' ||
    userRoleName === 'branch manager' ||
    userRoleName === 'credit manager' ||
    userRoleName === 'manager' ||
    userRoleName === 'officer' ||
    userRoleName === 'operations' ||
    userRoleName === 'collections officer' ||
    userRoleName === 'collection officer';
  var isBlacklisted = client && client.status === 'blacklisted';

  useEffect(function () { window.__openLightbox = setLightbox; return function () { delete window.__openLightbox; }; }, []);

  useEffect(function () {
    function handleKey(e) {
      if (e.key === 'Escape') {
        if (lightbox) setLightbox(null);
        else if (editModalOpen) setEditModalOpen(false);
        else if (profileModalOpen) setProfileModalOpen(false);
        else if (blacklistModalOpen) setBlacklistModalOpen(false);
        else if (limitModalOpen) setLimitModalOpen(false);
        else if (interactionModalOpen) setInteractionModalOpen(false);
        else if (isMobileSidebarOpen) setIsMobileSidebarOpen(false);
      }
    }
    window.addEventListener('keydown', handleKey);
    return function () { window.removeEventListener('keydown', handleKey); };
  }, [lightbox, editModalOpen, profileModalOpen, blacklistModalOpen, limitModalOpen, interactionModalOpen, isMobileSidebarOpen]);

  useEffect(function () {
    document.body.style.overflow = (lightbox || interactionModalOpen || editModalOpen || profileModalOpen || blacklistModalOpen || limitModalOpen) ? 'hidden' : '';
    return function () { document.body.style.overflow = ''; };
  }, [lightbox, interactionModalOpen, editModalOpen, profileModalOpen, blacklistModalOpen, limitModalOpen]);

  useEffect(function () {
    api.get('/clients/' + id).then(function (res) { setClient(res.data.data); }).catch(function () {});
    api.get('/users?isActive=true&limit=200').then(function (res) { setOfficers(res.data.data || []); }).catch(function () {});
  }, [id]);

  useEffect(function () {
    if (editSuccess) { var t = setTimeout(function () { setEditSuccess(false); }, 4000); return function () { clearTimeout(t); }; }
  }, [editSuccess]);

  var loadDocuments = useCallback(function () {
    if (loadedSections.has('documents')) return;
    setDocsLoading(true);
    api.get('/clients/' + id + '/documents').then(function (res) {
      setDocs(res.data.data); setLoadedSections(function (prev) { var s = new Set(prev); s.add('documents'); return s; });
    }).catch(function () {}).finally(function () { setDocsLoading(false); });
  }, [id, loadedSections]);

  var loadLoans = useCallback(function () {
    if (loadedSections.has('loans')) return;
    setLoansLoading(true);
    api.get('/clients/' + id + '/loans').then(function (res) {
      setLoans(res.data.data.loans || []); setLoanSummary(res.data.data.summary || null);
      setLoadedSections(function (prev) { var s = new Set(prev); s.add('loans'); return s; });
    }).catch(function () {}).finally(function () { setLoansLoading(false); });
  }, [id, loadedSections]);

  var loadInteractions = useCallback(function () {
    if (loadedSections.has('interactions')) return;
    setInteractionsLoading(true);
    api.get('/communications/client/' + id).then(function (res) {
      setInteractions(res.data.data || []); setLoadedSections(function (prev) { var s = new Set(prev); s.add('interactions'); return s; });
    }).catch(function () {}).finally(function () { setInteractionsLoading(false); });
  }, [id, loadedSections]);

  useEffect(function () {
    if (!client) return;
    if (activeSection === 'documents') loadDocuments();
    if (activeSection === 'loans') loadLoans();
    if (activeSection === 'interactions') loadInteractions();
  }, [activeSection, client, loadDocuments, loadLoans, loadInteractions]);

  var handleEditIdentity = function (data) {
    setSavingClient(true);
    api.put('/clients/' + id, data).then(function (res) {
      setClient(res.data.data); setEditModalOpen(false); setEditSuccess(true);
    }).catch(function (err) {
      alert(err.response?.data?.message || 'Failed to update client');
    }).finally(function () { setSavingClient(false); });
  };

  var handleProfilePictureUpload = function (e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { alert('Choose a JPG, PNG or WebP image.'); e.target.value = ''; return; }
    if (file.size > 5 * 1024 * 1024) { alert('Profile picture must be 5 MB or smaller.'); e.target.value = ''; return; }
    var form = new FormData();
    form.append('document', file);
    form.append('type', 'passport');
    setProfilePictureUploading(true);
    api.post('/clients/' + id + '/profile-photo', form)
      .then(async function (res) {
        var saved = res.data && res.data.data ? res.data.data : {};
        var savedPath = saved.passportPhotoUrl || saved.profilePicture || res.data.passportPhotoUrl || res.data.profilePicture;
        if (!savedPath) throw new Error('The server did not return the saved profile picture path. Please check the backend update files.');
        setClient(function (current) { return Object.assign({}, current, saved, { profilePicture: savedPath, passportPhotoUrl: savedPath, _profilePictureVersion: Date.now() }); });
        try { var fresh = await api.get('/clients/' + id); if (fresh.data && fresh.data.data) setClient(function (current) { return Object.assign({}, current, fresh.data.data, { profilePicture: fresh.data.data.profilePicture || fresh.data.data.passportPhotoUrl || savedPath, passportPhotoUrl: fresh.data.data.passportPhotoUrl || savedPath, _profilePictureVersion: Date.now() }); }); } catch (_) {}
        setEditSuccess(true);
      })
      .catch(function (err) { alert(err.response?.data?.message || 'Unable to upload profile picture.'); })
      .finally(function () { setProfilePictureUploading(false); if (profilePictureInputRef.current) profilePictureInputRef.current.value = ''; });
  };

  var handleProfileSave = function (data, homeFile, businessFile) {
    setSavingClient(true);
    api.put('/clients/' + id, data).then(function (res) {
      var updated = res.data.data;
      setClient(updated);

      var uploads = [];
      if (homeFile) uploads.push({ file: homeFile, type: 'home' });
      if (businessFile) uploads.push({ file: businessFile, type: 'business' });

      return uploads.reduce(function (promise, item) {
        return promise.then(function () {
          var fd = new FormData();
          fd.append('document', item.file);
          fd.append('type', item.type);
          return api.post('/clients/' + id + '/profile-photo', fd)
            .then(function (photoRes) { setClient(photoRes.data.data); });
        });
      }, Promise.resolve());
    }).then(function () {
      setProfileModalOpen(false);
      setEditSuccess(true);
    }).catch(function (err) {
      alert(err.response?.data?.message || 'Failed to update profile details');
    }).finally(function () { setSavingClient(false); });
  };

  var handleBlacklist = function (reason) {
    setBlacklistLoading(true);
    var action = isBlacklisted ? 'remove' : 'blacklist';
    api.put('/clients/' + id + '/blacklist', { action: action, reason: reason }).then(function (res) {
      setClient(res.data.data); setBlacklistModalOpen(false);
    }).catch(function (err) {
      alert(err.response?.data?.message || 'Failed to update blacklist status');
    }).finally(function () { setBlacklistLoading(false); });
  };

  var handleLimitChange = function (limit, reason) {
    setLimitLoading(true);
    api.put('/clients/' + id + '/limit', { customLoanLimit: limit, customLimitReason: reason }).then(function (res) {
      setClient(res.data.data); setLimitModalOpen(false);
    }).catch(function (err) {
      alert(err.response?.data?.message || 'Failed to update loan limit');
    }).finally(function () { setLimitLoading(false); });
  };

  var handleAssignOfficer = function (field, officerId) {
    setAssigning(true);
    var payload = {}; payload[field] = officerId || null;
    api.put('/clients/' + id + '/assign-officer', payload).then(function (res) {
      setClient(res.data.data);
    }).catch(function (err) { alert(err.response?.data?.message || 'Failed to assign officer'); })
      .finally(function () { setAssigning(false); });
  };

  var handleDocUpload = function (e) {
    var file = e.target.files[0];
    if (!file) return;
    if (!uploadDocType) { setUploadError('Please select a document type first.'); return; }
    setUploadError(''); setUploadingDoc(true);
    var fd = new FormData();
    /* FIX #3: field name must be 'document' to match backend upload.single('document') */
    fd.append('document', file);
    fd.append('documentType', uploadDocType);
    if (uploadDocNotes) fd.append('notes', uploadDocNotes);
    api.post('/clients/' + id + '/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(function (res) {
      setDocs(function (prev) { return [res.data.data].concat(prev); });
      setUploadDocType(''); setUploadDocNotes(''); setUploadSuccess(true);
      setTimeout(function () { setUploadSuccess(false); }, 3000);
      if (docFileRef.current) docFileRef.current.value = '';
    }).catch(function (err) { setUploadError(err.response?.data?.message || 'Upload failed'); }).finally(function () { setUploadingDoc(false); });
  };

  var handleSaveInteraction = function () {
    if (!newInteractionContent.trim()) return;
    setSavingInteraction(true);
    api.post('/communications', {
      client: id,
      conversationMethod: newInteractionType === 'note' ? 'other' : newInteractionType,
      conversationPurpose: '',
      conversationOutcome: 'Contactable',
      content: newInteractionContent.trim(),
      nextSteps: 'Call again',
    }).then(function (res) {
      setInteractions(function (prev) { return [res.data.data].concat(prev); });
      setNewInteractionContent(''); setInteractionModalOpen(false);
    }).catch(function () {}).finally(function () { setSavingInteraction(false); });
  };

  var handleRecoverRefs = function () {
    if (recoveringRefs || recoveredClientIdRef.current === id) return;
    setRecoveringRefs(true); recoveredClientIdRef.current = id;
    api.post('/clients/' + id + '/recover-references').then(function (res) {
      setClient(res.data.data); setRecoveringRefs(false);
    }).catch(function () {
      api.put('/clients/' + id + '/recover-references').then(function (res2) {
        setClient(res2.data.data);
      }).catch(function () {}).finally(function () { setRecoveringRefs(false); });
    });
  };

  var handleDownloadDoc = function (docId) {
    setDownloadingDocId(docId);
    api.get('/clients/documents/' + docId + '/download', { responseType: 'blob' }).then(function (res) {
      var url = window.URL.createObjectURL(new Blob([res.data]));
      var a = document.createElement('a'); a.href = url;
      a.setAttribute('download', 'document');
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    }).catch(function () {}).finally(function () { setDownloadingDocId(null); });
  };

  if (!client) {
    return (
      <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
        <style>{INJECTED_STYLES}</style>
        <div style={{ display: 'flex', gap: 20 }}>
          <div className="desktop-sidebar" style={{ width: 280, flexShrink: 0 }}>
            <SkeletonPulse height={200} borderRadius={16} />
            <div style={{ marginTop: 16 }}><SkeletonPulse height={400} borderRadius={16} /></div>
          </div>
          <div style={{ flex: 1 }}><SkeletonPulse height={600} borderRadius={16} /></div>
        </div>
      </div>
    );
  }

  var addr = client.address || {};

  function SmallBtn(props) {
    return (
      <button onClick={props.onClick} disabled={props.disabled} title={props.title || props.label} style={{
        padding: '6px 14px', borderRadius: 8, border: '1px solid ' + (props.borderColor || COLORS.gray200),
        background: props.bg || '#fff', color: props.color || COLORS.gray600,
        fontSize: 12, fontWeight: 600, cursor: props.disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        transition: 'all 0.2s', whiteSpace: 'nowrap',
      }}>
        {props.loading ? <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> : null}
        {props.icon || null} {props.label}
      </button>
    );
  }

  function NavItem(sec) {
    var isActive = activeSection === sec.id;
    return (
      <button key={sec.id} onClick={function () { setActiveSection(sec.id); setIsMobileSidebarOpen(false); }}
        style={{
          width: '100%', padding: '10px 14px', borderRadius: 10, border: 'none',
          background: isActive ? COLORS.primaryLight : 'transparent',
          color: isActive ? COLORS.primary : COLORS.gray600, fontSize: 13, fontWeight: isActive ? 600 : 500,
          cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10,
          transition: 'all 0.2s ease',
        }}>
        <span style={{ fontSize: 16 }}>{sec.icon}</span>
        <span>{sec.label}</span>
      </button>
    );
  }

  /* ═══════════════════════════════════════════════════════════════════
     SECTION RENDERERS
     ═══════════════════════════════════════════════════════════════════ */

  function renderPersonalSection() {
    return (
      <SectionCard icon="👤" title="Personal Information"
        action={canEditClient ? <SmallBtn icon="✏️" label="Edit" onClick={function () { setEditModalOpen(true); }} /> : null}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
          <InfoRow label="First Name" value={client.firstName} />
          <InfoRow label="Last Name" value={client.lastName} />
          <InfoRow label="Client Number" value={client.clientNumber} />
          <InfoRow label="Phone" value={client.phone} />
          <InfoRow label="Email" value={client.email} />
          <InfoRow label="National ID" value={client.nationalId} />
          <InfoRow label="Date of Birth" value={client.dateOfBirth ? formatDate(client.dateOfBirth) : ''} />
          <InfoRow label="Gender" value={client.gender ? client.gender.charAt(0).toUpperCase() + client.gender.slice(1) : ''} />
          <InfoRow label="Occupation" value={client.occupation} />
          <InfoRow label="Employer" value={client.employer} />
          <InfoRow label="Monthly Income" value={client.monthlyIncome ? formatCurrency(client.monthlyIncome) : ''} />
          <InfoRow label="Marital Status" value={client.maritalStatus ? client.maritalStatus.charAt(0).toUpperCase() + client.maritalStatus.slice(1) : ''} />
          <InfoRow label="Risk Rating" value={
            client.riskRating ? (
              <span style={{
                padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: client.riskRating === 'low' ? COLORS.successLight : client.riskRating === 'medium' ? COLORS.warningLight : client.riskRating === 'high' ? COLORS.dangerLight : '#fef2f2',
                color: client.riskRating === 'low' ? '#065f46' : client.riskRating === 'medium' ? '#92400e' : client.riskRating === 'high' ? '#991b1b' : '#7f1d1d',
              }}>{client.riskRating.charAt(0).toUpperCase() + client.riskRating.slice(1)}</span>
            ) : ''
          } />
          <InfoRow label="Branch" value={client.branch ? client.branch.name : ''} />
          <InfoRow label="Assigned To" value={client.assignedTo ? (client.assignedTo.firstName + ' ' + client.assignedTo.lastName) : ''} />
          {canReassign && <div style={{ gridColumn: '1 / -1', marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 10 }}>
            <div><div style={{fontSize:11,fontWeight:800,color:COLORS.gray500,textTransform:'uppercase',marginBottom:5}}>Loan Officer</div><select disabled={assigning} value={client.loanOfficer?._id || ''} onChange={function(e){handleAssignOfficer('loanOfficerId', e.target.value);}} style={{ padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', width:'100%' }}><option value="">Not assigned</option>{officers.filter(function(o){ return String(o.role?.name || o.roleName || o.role || '').toLowerCase() === 'loan officer'; }).map(function(o){ return <option key={o._id} value={o._id}>{o.firstName} {o.lastName}</option>; })}</select></div>
            <div><div style={{fontSize:11,fontWeight:800,color:COLORS.gray500,textTransform:'uppercase',marginBottom:5}}>Collection Officer</div><select disabled={assigning} value={client.collectionOfficer?._id || ''} onChange={function(e){handleAssignOfficer('collectionOfficerId', e.target.value);}} style={{ padding: 9, borderRadius: 8, border: '1px solid #cbd5e1', width:'100%' }}><option value="">Not assigned</option>{officers.filter(function(o){ return String(o.role?.name || o.roleName || o.role || '').toLowerCase() === 'collection officer'; }).map(function(o){ return <option key={o._id} value={o._id}>{o.firstName} {o.lastName}</option>; })}</select></div>
            <span style={{gridColumn:'1 / -1',fontSize:12,color:COLORS.gray500}}>{assigning ? 'Assigning…' : 'Admin, General Manager, Manager and Branch Manager can reassign both responsible officers.'}</span>
          </div>}
          <InfoRow label="Loan Officer" value={client.loanOfficer ? (client.loanOfficer.firstName + ' ' + client.loanOfficer.lastName) : ''} />
          <InfoRow label="Collection Officer" value={client.collectionOfficer ? (client.collectionOfficer.firstName + ' ' + client.collectionOfficer.lastName) : ''} />
          <InfoRow label="Status" value={<StatusBadge status={client.status} map={CLIENT_STATUS_MAP} />} />
          {client.approvedBy && <InfoRow label="Approved By" value={client.approvedBy.firstName + ' ' + client.approvedBy.lastName + (client.approvedAt ? ' on ' + formatDate(client.approvedAt) : '')} />}
          {client.rejectionReason && <InfoRow label="Rejection / Dormant Reason" value={client.rejectionReason} cols={2} />}
          {client.notes && <InfoRow label="Notes" value={client.notes} cols={2} />}
          {client.convertedFromLead && <InfoRow label="Converted From Lead" value={client.convertedFromLead.firstName + ' ' + client.convertedFromLead.lastName + ' (' + client.convertedFromLead.phone + ')'} cols={2} />}
        </div>
      </SectionCard>
    );
  }

  function renderNextOfKinSection() {
    var nok = client.nextOfKin || {};
    return (
      <SectionCard icon="👨‍👩‍👧" title="Next of Kin">
        {!nok.name ? (
          <EmptyState icon="👨‍👩‍👧" title="No Next of Kin" description="No next of kin information has been recorded." />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
            <InfoRow label="Name" value={nok.name} />
            <InfoRow label="Relationship" value={nok.relationship} />
            <InfoRow label="Phone" value={nok.phone} />
            <InfoRow label="National ID" value={nok.nationalId} />
          </div>
        )}
      </SectionCard>
    );
  }

  function renderReferencesSection() {
    var refs = client.references || [];
    return (
      <SectionCard icon="📋" title="References" count={refs.length}
        action={refs.length === 0 ? <SmallBtn icon="🔄" label="Recover from Loans" loading={recoveringRefs} onClick={handleRecoverRefs} /> : null}>
        {refs.length === 0 ? (
          <EmptyState icon="📋" title="No References" description="No references have been added. Click recover to auto-fill from past loan guarantors."
            action={<SmallBtn icon="🔄" label="Recover from Loans" loading={recoveringRefs} onClick={handleRecoverRefs} />} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {refs.map(function (ref, idx) {
              return (
                <div key={idx} style={{ padding: 14, background: COLORS.gray50, borderRadius: 10, border: '1px solid ' + COLORS.gray200, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, alignItems: 'center' }}>
                  <div><span style={{ fontSize: 10, fontWeight: 600, color: COLORS.gray400, textTransform: 'uppercase' }}>Name</span><p style={{ margin: '2px 0 0', fontSize: 14, fontWeight: 600, color: COLORS.gray800 }}>{ref.name || '—'}</p></div>
                  <div><span style={{ fontSize: 10, fontWeight: 600, color: COLORS.gray400, textTransform: 'uppercase' }}>Phone</span><p style={{ margin: '2px 0 0', fontSize: 14, color: COLORS.gray800 }}>{ref.phone || '—'}</p></div>
                  <div><span style={{ fontSize: 10, fontWeight: 600, color: COLORS.gray400, textTransform: 'uppercase' }}>Relationship</span><p style={{ margin: '2px 0 0', fontSize: 14, color: COLORS.gray800 }}>{ref.relationship || '—'}</p></div>
                  <div><span style={{ fontSize: 10, fontWeight: 600, color: COLORS.gray400, textTransform: 'uppercase' }}>ID Number</span><p style={{ margin: '2px 0 0', fontSize: 14, color: COLORS.gray800 }}>{ref.idNumber || '—'}</p></div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    );
  }

  function renderHomeAddressSection() {
    return (
      <SectionCard icon="🏠" title="Home Address" action={canEditProfileDetails ? <SmallBtn icon="✏️" label="Edit Home Details" onClick={function(){setProfileModalOpen(true);}} bg={COLORS.primaryLight} color={COLORS.primary} borderColor="#c7d2fe" /> : null}>
        {(!addr.street && !addr.city && !addr.state) ? (
          <EmptyState icon="🏠" title="No Address" description="No home address information has been recorded." />
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
              <InfoRow label="Street" value={addr.street} />
              <InfoRow label="City" value={addr.city} />
              <InfoRow label="Home County" value={addr.county || addr.state} />
              <InfoRow label="Postal Code" value={addr.postalCode} />
              <InfoRow label="Country" value={addr.country} />
              <InfoRow label="Address Type" value={addr.type} />
            </div>
            {addr.description && <InfoRow label="Description" value={addr.description} cols={2} />}
            {client.gpsLocation && (client.gpsLocation.latitude || client.gpsLocation.longitude) && (
              <div style={{ marginTop: 12, padding: 10, background: COLORS.gray50, borderRadius: 8, fontSize: 13, color: COLORS.gray600 }}>
                📍 GPS: {client.gpsLocation.latitude}, {client.gpsLocation.longitude}
              </div>
            )}
            {client.homePhotos && client.homePhotos.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Home Photos</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {client.homePhotos.map(function (photo, idx) { return <ClickableImage key={idx} src={photo.image} alt="Home photo" caption={photo.description || 'Home photo'} />; })}
                </div>
              </div>
            )}
            {client.homeDescription && <div style={{ marginTop: 12 }}><InfoRow label="Home Description" value={client.homeDescription} cols={2} /></div>}
          </div>
        )}
      </SectionCard>
    );
  }

  function renderBusinessSection() {
    return (
      <SectionCard icon="💼" title="Business Information" action={canEditProfileDetails ? <SmallBtn icon="✏️" label="Edit Business Details" onClick={function(){setProfileModalOpen(true);}} bg={COLORS.primaryLight} color={COLORS.primary} borderColor="#c7d2fe" /> : null}>
        {(!client.businessName && !client.businessType) ? (
          <EmptyState icon="💼" title="No Business Info" description="No business information has been recorded." />
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
              <InfoRow label="Business Name" value={client.businessName} />
              <InfoRow label="Business Type" value={client.businessType} />
              <InfoRow label="Business Sector" value={client.businessSector} />
              <InfoRow label="Business Address" value={client.businessAddress} />
            </div>
            {client.businessGpsLocation && (client.businessGpsLocation.latitude || client.businessGpsLocation.longitude) && (
              <div style={{ padding: 10, background: COLORS.gray50, borderRadius: 8, fontSize: 13, color: COLORS.gray600, marginBottom: 16 }}>
                📍 Business GPS: {client.businessGpsLocation.latitude}, {client.businessGpsLocation.longitude}
              </div>
            )}
            {client.businessPhotos && client.businessPhotos.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.gray500, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Business Photos</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {client.businessPhotos.map(function (photo, idx) { return <ClickableImage key={idx} src={photo.image} alt="Business photo" caption={photo.description || 'Business photo'} />; })}
                </div>
              </div>
            )}
            {client.businessDescription && <InfoRow label="Business Description" value={client.businessDescription} cols={2} />}
          </div>
        )}
      </SectionCard>
    );
  }

  /* ═══════════════════════════════════════════════════════════════════
     ★ LOAN LIMIT SECTION
     ═══════════════════════════════════════════════════════════════════ */
  function renderLoanLimitSection() {
    var approvedLimit = client.recommendedLoanLimit || 0;
    var customLimit = client.customLoanLimit || 0;
    var effectiveLimit = customLimit > 0 ? customLimit : approvedLimit;
    var hasCustom = customLimit > 0;
    var hasApproved = approvedLimit > 0;
    var productName = client.recommendedLoanProduct
      ? (client.recommendedLoanProduct.name || client.recommendedLoanProduct.productCode || '')
      : '';
    var productMax = client.recommendedLoanProduct
      ? (client.recommendedLoanProduct.maxAmount || 0)
      : 0;
    var limitSetBy = client.customLimitSetBy;
    var limitSetAt = client.customLimitSetAt;
    var limitReason = client.customLimitReason;
    var isApproved = client.status === 'approved' || client.status === 'dormant';

    return (
      <SectionCard
        icon="💳"
        title="Loan Limit"
        action={canSetLimit ? (
          <SmallBtn icon="✏️" label="Adjust Limit" onClick={function () { setLimitModalOpen(true); }} bg={COLORS.primaryLight} color={COLORS.primary} borderColor="#c7d2fe" />
        ) : null}
      >
        {!isApproved && (
          <div style={{ padding: '14px 18px', background: COLORS.warningLight, border: '1px solid ' + COLORS.warningBorder, borderRadius: 12, marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: '#92400e', lineHeight: 1.5 }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
            <div><strong>Client not yet approved.</strong> Loan limits are typically set during the approval process. Once this client is approved, you can adjust their limit here.</div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
          {/* Approved Limit card */}
          <div style={{ padding: 20, borderRadius: 14, border: '1px solid ' + COLORS.gray200, background: hasApproved ? COLORS.successLight : COLORS.gray50, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -8, right: -8, width: 48, height: 48, borderRadius: '50%', background: hasApproved ? 'rgba(16,185,129,0.08)' : 'transparent' }} />
            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.gray400, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 }}>
              {hasApproved ? '✅ Approved Limit' : 'Recommended Limit'}
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: hasApproved ? '#065f46' : COLORS.gray700, letterSpacing: '-0.5px' }}>
              {formatCurrency(approvedLimit)}
            </div>
            {productName && (
              <div style={{ marginTop: 8, fontSize: 11, color: COLORS.gray500, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ padding: '1px 8px', borderRadius: 6, background: COLORS.gray100, fontSize: 10, fontWeight: 600, color: COLORS.gray600 }}>{productName}</span>
                {productMax > 0 && <span style={{ color: COLORS.gray400 }}>max {formatCurrency(productMax)}</span>}
              </div>
            )}
            {!hasApproved && <div style={{ marginTop: 8, fontSize: 11, color: COLORS.gray400, fontStyle: 'italic' }}>No limit set at approval</div>}
          </div>

          {/* Custom Override card */}
          <div style={{ padding: 20, borderRadius: 14, border: '1px solid ' + (hasCustom ? COLORS.warningBorder : COLORS.gray200), background: hasCustom ? COLORS.warningLight : COLORS.gray50, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -8, right: -8, width: 48, height: 48, borderRadius: '50%', background: hasCustom ? 'rgba(245,158,11,0.08)' : 'transparent' }} />
            <div style={{ fontSize: 11, fontWeight: 600, color: hasCustom ? '#92400e' : COLORS.gray400, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 }}>
              {hasCustom ? '🔧 Custom Override' : 'Custom Override'}
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: hasCustom ? '#92400e' : COLORS.gray400, letterSpacing: '-0.5px' }}>
              {hasCustom ? formatCurrency(customLimit) : '—'}
            </div>
            {!hasCustom && <div style={{ marginTop: 8, fontSize: 11, color: COLORS.gray400, fontStyle: 'italic' }}>No override applied</div>}
            {hasCustom && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#92400e', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {limitSetBy && <span>By: <strong>{typeof limitSetBy === 'object' ? (limitSetBy.firstName || '') + ' ' + (limitSetBy.lastName || '') : 'System'}</strong></span>}
                {limitSetAt && <span>On: {formatDate(limitSetAt)}</span>}
              </div>
            )}
          </div>

          {/* Effective Limit card */}
          <div style={{ padding: 20, borderRadius: 14, border: '2px solid #c7d2fe', background: COLORS.primaryLight, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -8, right: -8, width: 48, height: 48, borderRadius: '50%', background: 'rgba(99,102,241,0.1)' }} />
            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.primaryDark, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 }}>💳 Effective Limit</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: COLORS.primary, letterSpacing: '-0.5px' }}>{formatCurrency(effectiveLimit)}</div>
            <div style={{ marginTop: 8, fontSize: 11, color: COLORS.primaryDark, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ padding: '2px 8px', borderRadius: 6, fontWeight: 700, fontSize: 10, background: hasCustom ? COLORS.warningLight : COLORS.successLight, color: hasCustom ? '#92400e' : '#065f46', border: '1px solid ' + (hasCustom ? COLORS.warningBorder : COLORS.successBorder) }}>
                {hasCustom ? 'CUSTOM' : 'APPROVED'}
              </span>
              <span style={{ color: COLORS.gray500 }}>{hasCustom ? 'Override active' : 'Base limit'}</span>
            </div>
          </div>
        </div>

        {/* Override reason */}
        {hasCustom && limitReason && (
          <div style={{ padding: '14px 18px', background: COLORS.gray50, border: '1px solid ' + COLORS.gray200, borderRadius: 12, marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.gray500, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Override Reason</div>
            <p style={{ margin: 0, fontSize: 14, color: COLORS.gray700, lineHeight: 1.6 }}>{limitReason}</p>
          </div>
        )}

        {/* Utilization */}
        {effectiveLimit > 0 && loanSummary && (
          <div style={{ padding: '14px 18px', background: '#f0fdf4', border: '1px solid ' + COLORS.successBorder, borderRadius: 12, marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>📊 Limit Utilization</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              <div><div style={{ fontSize: 10, color: '#065f46', fontWeight: 500 }}>Total Borrowed</div><div style={{ fontSize: 16, fontWeight: 700, color: '#065f46', marginTop: 2 }}>{formatCurrency(loanSummary.totalBorrowed || 0)}</div></div>
              <div><div style={{ fontSize: 10, color: '#065f46', fontWeight: 500 }}>Outstanding</div><div style={{ fontSize: 16, fontWeight: 700, color: '#065f46', marginTop: 2 }}>{formatCurrency(loanSummary.totalOutstanding || 0)}</div></div>
              <div><div style={{ fontSize: 10, color: '#065f46', fontWeight: 500 }}>Available</div><div style={{ fontSize: 16, fontWeight: 700, color: '#065f46', marginTop: 2 }}>{formatCurrency(Math.max(0, effectiveLimit - (loanSummary.totalOutstanding || 0)))}</div></div>
            </div>
            {effectiveLimit > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#065f46', marginBottom: 4 }}>
                  <span>Utilization</span>
                  <span>{Math.round(((loanSummary.totalOutstanding || 0) / effectiveLimit) * 100)}%</span>
                </div>
                <div style={{ height: 8, background: '#d1fae5', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 4,
                    width: Math.min(100, ((loanSummary.totalOutstanding || 0) / effectiveLimit) * 100) + '%',
                    background: ((loanSummary.totalOutstanding || 0) / effectiveLimit) > 0.8 ? COLORS.danger : ((loanSummary.totalOutstanding || 0) / effectiveLimit) > 0.5 ? COLORS.warning : COLORS.success,
                    transition: 'width 0.5s ease',
                  }} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* No limit at all for approved client */}
        {!hasApproved && !hasCustom && isApproved && (
          <div style={{ padding: '14px 18px', background: COLORS.dangerLight, border: '1px solid ' + COLORS.dangerBorder, borderRadius: 12, display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: '#991b1b', lineHeight: 1.5 }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>🔴</span>
            <div><strong>No loan limit set.</strong> This approved client has no borrowing limit. Click "Adjust Limit" to set one before they can apply for loans.</div>
          </div>
        )}

        {!canSetLimit && isApproved && (
          <div style={{ marginTop: 8, fontSize: 12, color: COLORS.gray400, fontStyle: 'italic' }}>Only Admin, General Manager, or Branch Manager can adjust loan limits.</div>
        )}
      </SectionCard>
    );
  }

  function renderInteractionsSection() {
    var filtered = interactions;
    if (interactionFilter !== 'all') {
      filtered = interactions.filter(function (i) { return i.type === interactionFilter; });
    }
    return (
      <SectionCard icon="💬" title="Interactions" count={filtered.length}
        action={<SmallBtn icon="➕" label="Add Note" onClick={function () { setInteractionModalOpen(true); }} />}>
        <div style={{ marginBottom: 16, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[{ value: 'all', label: 'All' }].concat(INTERACTION_TYPES).map(function (t) {
            var active = interactionFilter === t.value;
            return (
              <button key={t.value} onClick={function () { setInteractionFilter(t.value); }}
                style={{ padding: '4px 12px', borderRadius: 20, border: '1px solid ' + (active ? COLORS.primary : COLORS.gray200), background: active ? COLORS.primaryLight : '#fff', color: active ? COLORS.primary : COLORS.gray500, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                {t.icon} {t.label}
              </button>
            );
          })}
        </div>
        {interactionsLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{[1, 2, 3].map(function (i) { return <SkeletonPulse key={i} height={60} borderRadius={10} />; })}</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon="💬" title="No Interactions" description="No interactions recorded for this client yet." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map(function (inter) {
              var typeCfg = INTERACTION_TYPES.find(function (t) { return t.value === inter.type; }) || INTERACTION_TYPES[INTERACTION_TYPES.length - 1];
              return (
                <div key={inter._id} style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid ' + COLORS.gray200, background: '#fff', cursor: 'pointer', transition: 'all 0.2s', borderLeft: '4px solid ' + typeCfg.color }}
                  onClick={function () { setExpandedInteraction(expandedInteraction === inter._id ? null : inter._id); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 18 }}>{typeCfg.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.gray800 }}>{typeCfg.label}</span>
                        <span style={{ fontSize: 11, color: COLORS.gray400, padding: '1px 8px', background: COLORS.gray100, borderRadius: 6 }}>{inter.stepTitle || inter.step || ''}</span>
                      </div>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: COLORS.gray600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: expandedInteraction === inter._id ? 'normal' : 'nowrap' }}>{inter.content}</p>
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: COLORS.gray400 }}>{inter.recordedBy ? (inter.recordedBy.firstName + ' ' + inter.recordedBy.lastName) : ''}</div>
                      <div style={{ fontSize: 10, color: COLORS.gray400, marginTop: 2 }}>{formatDate(inter.createdAt)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    );
  }

  function renderCollateralSection() {
    var coll = client.collateral || [];
    return (
      <SectionCard icon="🔒" title="Collateral" count={coll.length}>
        {coll.length === 0 ? (
          <EmptyState icon="🔒" title="No Collateral" description="No collateral has been registered for this client." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {coll.map(function (item, idx) {
              return (
                <div key={idx} style={{ padding: 16, background: COLORS.gray50, borderRadius: 12, border: '1px solid ' + COLORS.gray200, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  {item.image ? <ClickableImage src={item.image} alt={item.type || 'Collateral'} width={100} height={80} caption={item.type || 'Collateral'} /> : <div style={{ width: 100, height: 80, borderRadius: 8, background: COLORS.gray200, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0 }}>🔒</div>}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.gray800, marginBottom: 4 }}>{item.type || 'Collateral'}</div>
                    {item.description && <p style={{ margin: '0 0 6px', fontSize: 13, color: COLORS.gray600 }}>{item.description}</p>}
                    {item.estimatedValue > 0 && <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.success }}>{formatCurrency(item.estimatedValue)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    );
  }

  function renderDocumentsSection() {
    return (
      <SectionCard icon="📄" title="Documents" count={docs.length}
        action={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={uploadDocType} onChange={function (e) { setUploadDocType(e.target.value); }} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid ' + COLORS.gray200, fontSize: 12, color: COLORS.gray700, background: '#fff', cursor: 'pointer' }}>
              <option value="">Select type...</option>
              {DOC_TYPE_OPTIONS.map(function (o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
            </select>
            <input type="text" value={uploadDocNotes} onChange={function (e) { setUploadDocNotes(e.target.value); }} placeholder="Notes (optional)" style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid ' + COLORS.gray200, fontSize: 12, width: 140, color: COLORS.gray700 }} />
            <input type="file" ref={docFileRef} onChange={handleDocUpload} style={{ display: 'none' }} accept=".jpg,.jpeg,.png,.webp,.pdf" />
            <SmallBtn icon="📤" label={uploadingDoc ? 'Uploading...' : 'Upload'} loading={uploadingDoc} onClick={function () { if (docFileRef.current) docFileRef.current.click(); }} />
          </div>
        }>
        {uploadSuccess && <div style={{ padding: '10px 16px', background: COLORS.successLight, border: '1px solid ' + COLORS.successBorder, borderRadius: 10, marginBottom: 16, fontSize: 13, color: '#065f46', fontWeight: 500 }}>✅ Document uploaded successfully!</div>}
        {uploadError && <div style={{ padding: '10px 16px', background: COLORS.dangerLight, border: '1px solid ' + COLORS.dangerBorder, borderRadius: 10, marginBottom: 16, fontSize: 13, color: '#991b1b', fontWeight: 500 }}>❌ {uploadError}</div>}
        {docsLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{[1, 2, 3].map(function (i) { return <SkeletonPulse key={i} height={50} borderRadius={10} />; })}</div>
        ) : docs.length === 0 ? (
          <EmptyState icon="📄" title="No Documents" description="No KYC documents have been uploaded yet." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {docs.map(function (doc) {
              var statusCfg = getDocStatusConfig(doc.status);
              return (
                <div key={doc._id} style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid ' + COLORS.gray200, background: '#fff', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: COLORS.gray100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{doc.mimeType && doc.mimeType.includes('pdf') ? '📕' : '🖼️'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.gray800 }}>{getDocTypeLabel(doc.documentType)}</div>
                    <div style={{ fontSize: 11, color: COLORS.gray400, marginTop: 2 }}>{doc.originalName} · {(doc.fileSize / 1024).toFixed(1)}KB · {formatDate(doc.createdAt)}{doc.uploadedBy && <span> · {doc.uploadedBy.firstName} {doc.uploadedBy.lastName}</span>}</div>
                    {doc.notes && <div style={{ fontSize: 11, color: COLORS.gray500, marginTop: 2, fontStyle: 'italic' }}>{doc.notes}</div>}
                  </div>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: statusCfg.bg, color: statusCfg.color, border: '1px solid ' + statusCfg.border, flexShrink: 0 }}>{statusCfg.label}</span>
                  <button onClick={function () { handleDownloadDoc(doc._id); }} disabled={downloadingDocId === doc._id} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid ' + COLORS.gray200, background: '#fff', color: COLORS.gray600, fontSize: 12, cursor: downloadingDocId === doc._id ? 'wait' : 'pointer', flexShrink: 0 }}>{downloadingDocId === doc._id ? '⏳' : '📥'}</button>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    );
  }

  function renderLoansSection() {
    return (
      <SectionCard icon="💰" title="Loan History" count={loans.length}>
        {loansLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{[1, 2, 3].map(function (i) { return <SkeletonPulse key={i} height={80} borderRadius={10} />; })}</div>
        ) : loans.length === 0 ? (
          <EmptyState icon="💰" title="No Loans" description="This client has no loan history." />
        ) : (
          <div>
            {loanSummary && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
                {[
                  { label: 'Total Loans', value: loanSummary.totalLoans, color: COLORS.gray800 },
                  { label: 'Total Borrowed', value: formatCurrency(loanSummary.totalBorrowed), color: COLORS.primary },
                  { label: 'Total Paid', value: formatCurrency(loanSummary.totalPaid), color: COLORS.success },
                  { label: 'Outstanding', value: formatCurrency(loanSummary.totalOutstanding), color: loanSummary.totalOutstanding > 0 ? COLORS.warning : COLORS.gray600 },
                  { label: 'Active', value: loanSummary.activeLoans, color: COLORS.success },
                  { label: 'Overdue', value: loanSummary.overdueLoans, color: COLORS.danger },
                  { label: 'Closed', value: loanSummary.closedLoans, color: COLORS.gray500 },
                  { label: 'Collection Rate', value: (loanSummary.collectionRate || 0) + '%', color: COLORS.primaryDark },
                ].map(function (s, i) {
                  return (
                    <div key={i} style={{ padding: 12, background: COLORS.gray50, borderRadius: 10, border: '1px solid ' + COLORS.gray200 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.gray400, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: s.color, marginTop: 2 }}>{s.value}</div>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {loans.map(function (loan) {
                var statusColors = {
                  active: { bg: COLORS.successLight, color: '#065f46', border: COLORS.successBorder },
                  overdue: { bg: COLORS.dangerLight, color: '#991b1b', border: COLORS.dangerBorder },
                  defaulted: { bg: '#fef2f2', color: '#7f1d1d', border: COLORS.dangerBorder },
                  closed: { bg: COLORS.gray50, color: COLORS.gray500, border: COLORS.gray200 },
                  'written-off': { bg: COLORS.gray50, color: COLORS.gray400, border: COLORS.gray200 },
                  pending: { bg: COLORS.warningLight, color: '#92400e', border: COLORS.warningBorder },
                  approved: { bg: '#eff6ff', color: '#1e40af', border: '#bfdbfe' },
                  'under-review': { bg: '#f5f3ff', color: '#5b21b6', border: '#ddd6fe' },
                  processing: { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
                  disbursed: { bg: COLORS.successLight, color: '#065f46', border: COLORS.successBorder },
                };
                var sc = statusColors[loan.status] || statusColors.pending;
                return (
                  <div key={loan._id} style={{ padding: '14px 18px', borderRadius: 12, border: '1px solid ' + COLORS.gray200, background: '#fff', borderLeft: '4px solid ' + sc.color }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.gray800 }}>{loan.loanProduct ? loan.loanProduct.name : 'Loan'}</span>
                          <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 20, fontWeight: 600, background: sc.bg, color: sc.color, border: '1px solid ' + sc.border }}>{loan.status}</span>
                        </div>
                        <div style={{ fontSize: 12, color: COLORS.gray500, marginTop: 4 }}>{loan.loanNumber || '—'} · {loan.branch ? loan.branch.name : ''} · {loan.disbursementDate ? formatDate(loan.disbursementDate) : 'Not disbursed'}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}><div style={{ fontSize: 18, fontWeight: 800, color: COLORS.primary }}>{formatCurrency(loan.principalAmount || loan.disbursementAmount || 0)}</div></div>
                    </div>
                    {loan.totalRepayable > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: COLORS.gray500, marginBottom: 3 }}>
                          <span>Paid: {formatCurrency(loan.totalPaid || 0)}</span>
                          <span>{loan.paymentProgress || 0}%</span>
                        </div>
                        <div style={{ height: 6, background: COLORS.gray100, borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: 3, width: Math.min(100, loan.paymentProgress || 0) + '%', background: (loan.paymentProgress || 0) >= 100 ? COLORS.success : COLORS.primary, transition: 'width 0.5s ease' }} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </SectionCard>
    );
  }

  /* ═══════════════════════════════════════════════════════════════════
     SECTION ROUTER
     ═══════════════════════════════════════════════════════════════════ */
  function renderActiveSection() {
    switch (activeSection) {
      case 'personal': return renderPersonalSection();
      case 'nextOfKin': return renderNextOfKinSection();
      case 'references': return renderReferencesSection();
      case 'homeAddress': return renderHomeAddressSection();
      case 'business': return renderBusinessSection();
      case 'loanLimit': return renderLoanLimitSection();
      case 'interactions': return renderInteractionsSection();
      case 'collateral': return renderCollateralSection();
      case 'documents': return renderDocumentsSection();
      case 'loans': return renderLoansSection();
      default: return renderPersonalSection();
    }
  }

  var statusCfg = (CLIENT_STATUS_MAP || {})[client.status] || { label: client.status, color: COLORS.gray500, bg: COLORS.gray100 };

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1440, margin: '0 auto' }}>
      <style>{INJECTED_STYLES}</style>

      {/* Success toast */}
      {editSuccess && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 2000, padding: '12px 20px', borderRadius: 12, background: COLORS.successLight, border: '1px solid ' + COLORS.successBorder, color: '#065f46', fontSize: 13, fontWeight: 600, boxShadow: '0 8px 24px rgba(16,185,129,0.2)', animation: 'slideIn 0.3s ease', display: 'flex', alignItems: 'center', gap: 8 }}>
          ✅ Client updated successfully
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', animation: 'fadeIn 0.2s ease' }} onClick={function () { setLightbox(null); }}>
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <img src={lightbox.src} alt={lightbox.caption} style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 12, objectFit: 'contain' }} />
            {lightbox.caption && <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px 20px', background: 'linear-gradient(transparent, rgba(0,0,0,0.8))', borderRadius: '0 0 12px 12px', color: '#fff', fontSize: 13 }}>{lightbox.caption}</div>}
            <button onClick={function (e) { e.stopPropagation(); setLightbox(null); }} style={{ position: 'absolute', top: -12, right: -12, width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.9)', color: COLORS.gray700, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>✕</button>
          </div>
        </div>
      )}

      {/* Modals */}
      <ProfileDetailsModal isOpen={profileModalOpen} onClose={function(){setProfileModalOpen(false);}} onSave={handleProfileSave} saving={savingClient} client={client} />
      <EditIdentityModal isOpen={editModalOpen} onClose={function () { setEditModalOpen(false); }} onSave={handleEditIdentity} saving={savingClient} client={client} />
      <BlacklistModal isOpen={blacklistModalOpen} onClose={function () { setBlacklistModalOpen(false); }} onSave={handleBlacklist} saving={blacklistLoading} client={client} isBlacklisted={isBlacklisted} />
      <LoanLimitModal isOpen={limitModalOpen} onClose={function () { setLimitModalOpen(false); }} onSave={handleLimitChange} saving={limitLoading} client={client} />

      {/* Interaction modal */}
      {interactionModalOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', animation: 'fadeIn 0.2s ease' }} onClick={function (e) { if (e.target === e.currentTarget) setInteractionModalOpen(false); }}>
          <div style={{ background: '#fff', borderRadius: 20, width: '90%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 60px rgba(0,0,0,0.3)', animation: 'zoomIn 0.25s ease' }} onClick={function (e) { e.stopPropagation(); }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + COLORS.gray100, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: COLORS.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>💬</div>
                <div><h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: COLORS.gray800 }}>Add Interaction</h3><p style={{ margin: '2px 0 0', fontSize: 12, color: COLORS.gray400 }}>Record a note, call, visit, or other interaction</p></div>
              </div>
              <button onClick={function () { setInteractionModalOpen(false); }} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: COLORS.gray100, color: COLORS.gray500, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.gray700, marginBottom: 8 }}>Type</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {INTERACTION_TYPES.map(function (t) {
                    var active = newInteractionType === t.value;
                    return (<button key={t.value} onClick={function () { setNewInteractionType(t.value); }} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid ' + (active ? t.color : COLORS.gray200), background: active ? t.bg : '#fff', color: active ? t.color : COLORS.gray600, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>{t.icon} {t.label}</button>);
                  })}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: COLORS.gray700, marginBottom: 6 }}>Content <span style={{ color: COLORS.danger }}>*</span></label>
                <textarea value={newInteractionContent} onChange={function (e) { setNewInteractionContent(e.target.value); }} placeholder="What happened during this interaction..." rows={4} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '2px solid ' + COLORS.gray200, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box', color: COLORS.gray800, resize: 'vertical', fontFamily: 'inherit' }} onFocus={function (e) { e.target.style.borderColor = COLORS.primary; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.1)'; }} onBlur={function (e) { e.target.style.borderColor = COLORS.gray200; e.target.style.boxShadow = 'none'; }} />
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid ' + COLORS.gray100, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={function () { setInteractionModalOpen(false); }} style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid ' + COLORS.gray200, background: '#fff', color: COLORS.gray600, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveInteraction} disabled={savingInteraction || !newInteractionContent.trim()} style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: (!savingInteraction && newInteractionContent.trim()) ? COLORS.primary : COLORS.gray300, color: '#fff', fontSize: 14, fontWeight: 600, cursor: (!savingInteraction && newInteractionContent.trim()) ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 8, boxShadow: (!savingInteraction && newInteractionContent.trim()) ? '0 4px 12px rgba(99,102,241,0.3)' : 'none' }}>
                {savingInteraction ? <span><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Saving...</span> : <span>💬 Save</span>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile sidebar overlay */}
      {isMobileSidebarOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 800, background: 'rgba(15,23,42,0.4)', display: 'flex', justifyContent: 'flex-start' }} onClick={function () { setIsMobileSidebarOpen(false); }}>
          <div style={{ width: 280, height: '100%', background: '#fff', boxShadow: '4px 0 20px rgba(0,0,0,0.15)', padding: 16, overflowY: 'auto', animation: 'slideIn 0.25s ease' }} onClick={function (e) { e.stopPropagation(); }}>
            {SECTIONS.map(NavItem)}
          </div>
        </div>
      )}

      {/* Main layout */}
      <div style={{ display: 'flex', gap: 20 }}>

        {/* Desktop Sidebar */}
        <div className="desktop-sidebar" style={{ width: sidebarCollapsed ? 0 : 260, flexShrink: 0, transition: 'width 0.3s ease', overflow: 'hidden' }}>
          <div style={{ width: 260 }}>
            {/* Client card */}
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid ' + COLORS.gray200, overflow: 'hidden', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ padding: '20px 16px', background: 'linear-gradient(135deg, ' + COLORS.primary + ' 0%, ' + COLORS.primaryDark + ' 100%)', color: '#fff', textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, margin: '0 auto 10px', fontWeight: 700 }}>{(client.firstName || '')[0]}{(client.lastName || '')[0]}</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{client.firstName} {client.lastName}</div>
                <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>{client.clientNumber}</div>
                <div style={{ marginTop: 8 }}><span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>{statusCfg.label || client.status}</span></div>
              </div>
              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 12, color: COLORS.gray500, display: 'flex', alignItems: 'center', gap: 6 }}>📱 {client.phone}</div>
                {client.email && <div style={{ fontSize: 12, color: COLORS.gray500, display: 'flex', alignItems: 'center', gap: 6 }}>✉️ {client.email}</div>}
                {client.branch && <div style={{ fontSize: 12, color: COLORS.gray500, display: 'flex', alignItems: 'center', gap: 6 }}>🏢 {client.branch.name}</div>}
                <div style={{ fontSize: 12, color: COLORS.gray400, marginTop: 4 }}>Created {formatDate(client.createdAt)}</div>
              </div>
            </div>

            {/* Quick stats */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
              <div style={{ padding: 12, background: COLORS.primaryLight, borderRadius: 10, border: '1px solid #c7d2fe', textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.primary }}>{client.totalLoans || 0}</div>
                <div style={{ fontSize: 10, color: COLORS.primaryDark, fontWeight: 600, textTransform: 'uppercase' }}>Loans</div>
              </div>
              <div style={{ padding: 12, background: COLORS.successLight, borderRadius: 10, border: '1px solid ' + COLORS.successBorder, textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#065f46' }}>{client.activeLoans || 0}</div>
                <div style={{ fontSize: 10, color: '#065f46', fontWeight: 600, textTransform: 'uppercase' }}>Active</div>
              </div>
            </div>

            {/* Navigation */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.gray400, textTransform: 'uppercase', letterSpacing: '1px', padding: '4px 14px 8px' }}>Sections</div>
              {SECTIONS.map(NavItem)}
            </div>

            {/* Action buttons */}
            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 16, borderTop: '1px solid ' + COLORS.gray200 }}>
              {canEditClient && (
                <button onClick={function () { setEditModalOpen(true); }} style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid ' + COLORS.gray200, background: '#fff', color: COLORS.gray700, fontSize: 13, fontWeight: 500, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>✏️ Edit Identity</button>
              )}
              {canSetLimit && (
                /* FIX #2: was '1px solid '#c7d2fe' (two unjoined string literals) */
                <button onClick={function () { setLimitModalOpen(true); }} style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #c7d2fe', background: COLORS.primaryLight, color: COLORS.primary, fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>💳 Adjust Loan Limit</button>
              )}
              {canBlacklist && (
                <button onClick={function () { setBlacklistModalOpen(true); }} style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid ' + (isBlacklisted ? COLORS.successBorder : COLORS.dangerBorder), background: isBlacklisted ? COLORS.successLight : COLORS.dangerLight, color: isBlacklisted ? '#065f46' : '#991b1b', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isBlacklisted ? '✅ Remove Blacklist' : '🚫 Blacklist'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Professional client overview */}
          <div style={{
            background: '#fff',
            border: '1px solid ' + COLORS.gray200,
            borderRadius: 20,
            marginBottom: 16,
            overflow: 'hidden',
            boxShadow: '0 8px 28px rgba(15,23,42,0.06)',
          }}>
            <div style={{
              padding: '22px 24px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 52%, #ffffff 100%)',
              borderBottom: '1px solid ' + COLORS.gray200,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
                  <button className="mobile-sidebar-toggle" onClick={function () { setIsMobileSidebarOpen(true); }} style={{ width: 40, height: 40, borderRadius: 10, border: '1px solid ' + COLORS.gray200, background: '#fff', color: COLORS.gray600, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>☰</button>
                  <button onClick={function () { navigate(-1); }} style={{ width: 40, height: 40, borderRadius: 10, border: '1px solid ' + COLORS.gray200, background: '#fff', color: COLORS.gray600, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>←</button>
                  <div style={{
                    width: 68, height: 68, borderRadius: 18, flexShrink: 0, overflow: 'hidden', position: 'relative',
                    background: 'linear-gradient(135deg, ' + COLORS.primary + ', ' + COLORS.primaryDark + ')',
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 24, fontWeight: 800, boxShadow: '0 10px 22px rgba(79,70,229,0.22)',
                  }}>
                    <span data-profile-fallback="true" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{(client.firstName || '')[0]}{(client.lastName || '')[0]}</span>{(client.profilePicture || client.passportPhotoUrl) && <img src={getClientPhotoUrl(client.profilePicture || client.passportPhotoUrl, client._profilePictureVersion || client.updatedAt || client._id)} alt="Client profile" onError={function (e) { if (e.currentTarget) { e.currentTarget.onerror = null; e.currentTarget.style.display = 'none'; } }} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <h1 style={{ margin: 0, fontSize: 24, lineHeight: 1.15, fontWeight: 800, color: COLORS.gray900 }}>
                        {client.firstName} {client.lastName}
                      </h1>
                      <span style={{ padding: '5px 10px', borderRadius: 999, background: statusCfg.bg, color: statusCfg.color, fontSize: 11, fontWeight: 800, border: '1px solid ' + (statusCfg.color || COLORS.gray200) + '35' }}>
                        {statusCfg.label || client.status}
                      </span>
                      {isBlacklisted && <span style={{ padding: '5px 10px', borderRadius: 999, background: COLORS.dangerLight, color: '#991b1b', fontSize: 11, fontWeight: 800 }}>🚫 BLACKLISTED</span>}
                    </div>
                    <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 10, color: COLORS.gray500, fontSize: 12 }}>
                      <span style={{ fontWeight: 700, color: COLORS.gray700 }}>#{client.clientNumber || '—'}</span>
                      <span>•</span>
                      <span>🏢 {client.branch ? client.branch.name : 'No branch assigned'}</span>
                      <span>•</span>
                      <span>📅 Client since {client.createdAt ? formatDate(client.createdAt) : '—'}</span>
                    </div>
                    <div style={{ marginTop: 9, display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 13 }}>
                      <span style={{ color: COLORS.gray700, fontWeight: 600 }}>📱 {client.phone || 'No phone'}</span>
                      {client.email && <span style={{ color: COLORS.gray700, fontWeight: 600 }}>✉️ {client.email}</span>}
                    </div>
                  </div>
                </div>

                <div className="action-bar-buttons" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {(canEditProfileDetails || canEditClient) && <><input ref={profilePictureInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleProfilePictureUpload} style={{ display: 'none' }} /><SmallBtn icon="📷" label={profilePictureUploading ? 'Uploading…' : 'Profile Photo'} loading={profilePictureUploading} onClick={function () { if (!profilePictureUploading && profilePictureInputRef.current) profilePictureInputRef.current.click(); }} /></>}
                  {canEditClient && <SmallBtn icon="✏️" label="Edit" onClick={function () { setEditModalOpen(true); }} />}
                  {canSetLimit && <SmallBtn icon="💳" label="Loan Limit" onClick={function () { setLimitModalOpen(true); }} bg={COLORS.primaryLight} color={COLORS.primary} borderColor="#c7d2fe" />}
                  {canBlacklist && <SmallBtn icon={isBlacklisted ? '✅' : '🚫'} label={isBlacklisted ? 'Un-blacklist' : 'Blacklist'} onClick={function () { setBlacklistModalOpen(true); }} bg={isBlacklisted ? COLORS.successLight : COLORS.dangerLight} color={isBlacklisted ? '#065f46' : '#991b1b'} borderColor={isBlacklisted ? COLORS.successBorder : COLORS.dangerBorder} />}
                </div>
              </div>

              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))',
                gap: 10, marginTop: 20,
              }}>
                {[
                  { label: 'Total Loans', value: client.totalLoans || 0, icon: '📁', bg: '#f8fafc', color: COLORS.gray800 },
                  { label: 'Active Loans', value: client.activeLoans || 0, icon: '🟢', bg: COLORS.successLight, color: '#065f46' },
                  { label: 'Total Borrowed', value: formatCurrency(client.totalBorrowed || 0), icon: '💰', bg: '#eff6ff', color: '#1d4ed8' },
                  { label: 'Approved Limit', value: formatCurrency(client.customLoanLimit || client.recommendedLoanLimit || 0), icon: '💳', bg: COLORS.primaryLight, color: COLORS.primaryDark },
                ].map(function (m) {
                  return (
                    <div key={m.label} style={{
                      padding: '12px 14px', borderRadius: 13, background: m.bg,
                      border: '1px solid ' + COLORS.gray200, display: 'flex',
                      alignItems: 'center', gap: 10, minWidth: 0,
                    }}>
                      <div style={{ width: 34, height: 34, borderRadius: 10, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 6px rgba(15,23,42,0.06)' }}>{m.icon}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.gray500, textTransform: 'uppercase', letterSpacing: '.5px' }}>{m.label}</div>
                        <div style={{ marginTop: 2, fontSize: 17, fontWeight: 800, color: m.color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.value}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{
              padding: '14px 24px', display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 12, background: '#fff',
            }}>
              <div style={{ padding: '10px 12px', borderRadius: 11, background: COLORS.gray50, border: '1px solid ' + COLORS.gray200 }}>
                <div style={{ fontSize: 10, color: COLORS.gray400, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.5px' }}>Loan Officer</div>
                <div style={{ marginTop: 4, fontSize: 13, color: COLORS.gray800, fontWeight: 700 }}>{client.loanOfficer ? ((client.loanOfficer.firstName || '') + ' ' + (client.loanOfficer.lastName || '')).trim() : 'Not assigned'}</div>
              </div>
              <div style={{ padding: '10px 12px', borderRadius: 11, background: COLORS.gray50, border: '1px solid ' + COLORS.gray200 }}>
                <div style={{ fontSize: 10, color: COLORS.gray400, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.5px' }}>Collection Officer</div>
                <div style={{ marginTop: 4, fontSize: 13, color: COLORS.gray800, fontWeight: 700 }}>{client.collectionOfficer ? ((client.collectionOfficer.firstName || '') + ' ' + (client.collectionOfficer.lastName || '')).trim() : 'Not assigned'}</div>
              </div>
              <div style={{ padding: '10px 12px', borderRadius: 11, background: COLORS.gray50, border: '1px solid ' + COLORS.gray200 }}>
                <div style={{ fontSize: 10, color: COLORS.gray400, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.5px' }}>Risk Rating</div>
                <div style={{ marginTop: 4, fontSize: 13, fontWeight: 800, color: client.riskRating === 'high' || client.riskRating === 'very-high' ? '#dc2626' : client.riskRating === 'low' ? '#059669' : COLORS.gray800 }}>
                  {(client.riskRating || 'medium').replace(/-/g, ' ').toUpperCase()}
                </div>
              </div>
              <div style={{ padding: '10px 12px', borderRadius: 11, background: COLORS.gray50, border: '1px solid ' + COLORS.gray200 }}>
                <div style={{ fontSize: 10, color: COLORS.gray400, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.5px' }}>Recommended Product</div>
                <div style={{ marginTop: 4, fontSize: 13, color: COLORS.gray800, fontWeight: 700 }}>{client.recommendedLoanProduct ? client.recommendedLoanProduct.name : 'Not specified'}</div>
              </div>
            </div>
          </div>

          {isBlacklisted && (
            <div style={{
              marginBottom: 16, padding: '14px 18px', borderRadius: 14,
              background: 'linear-gradient(135deg,#fff1f2,#fef2f2)',
              border: '1px solid #fecdd3', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', gap: 12, flexWrap: 'wrap'
            }}>
              <div>
                <div style={{ fontWeight: 800, color: '#9f1239', fontSize: 14 }}>🚫 BLACKLISTED CLIENT</div>
                <div style={{ color: '#881337', fontSize: 12, marginTop: 3 }}>
                  {client.blacklistReason || 'This client has been blacklisted.'}
                  {client.blacklistedAt ? ' · ' + formatDate(client.blacklistedAt) : ''}
                </div>
              </div>
              {canBlacklist && <SmallBtn icon="↩️" label="Remove Blacklist" onClick={function () { setBlacklistModalOpen(true); }} bg="#fff" color="#9f1239" borderColor="#fda4af" />}
            </div>
          )}

          {/* Section content */}
          <div style={{ animation: 'fadeIn 0.3s ease' }}>{renderActiveSection()}</div>
        </div>
      </div>
    </div>
  );
}